import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { shouldSendCpfOnUpdate } from '../../../utils/cpfUtils';
import { cpfToSubmit } from '../cpfToSubmit';
import { PersonDetailData } from '../index';
import { Check, X, Cake, Calendar, AlertTriangle, Phone, PhoneOff, Copy, ChevronDown, ChevronUp, Clock, Building2, Link, RefreshCw, Users, ChevronLeft, ChevronRight as ChevronRightIcon, History, ZoomIn, Car, ArrowRight } from 'lucide-react';
import { fetchWithAuth, TimeZone, condoApi } from '../../../services/api';
import { getAbsoluteImageUrl } from '../../../utils/imageUrl';
import { usePersonFieldConfig } from '../../../hooks/usePersonFieldConfig';
import { CustomPersonField } from '../../../types/customer/base';
import { getOrderedFields } from '../../../utils/fieldConfigUtils';
import { DeviceOperations, DeviceInfo, CommunicationMode } from '../../../services/deviceCommunication';
import { DeviceIntegration } from '../../../integrations/devices';
import { api } from '../../../services/api';
import { formatCPF, canViewFullCPF, maskCPF } from '../../../utils/cpfFormatter';
import { joinFullName, splitFullName, validateFullName , sanitizePersonName } from '../../../utils/fullName';
import { DateInput } from '../../ui/DateInput';
import toast from 'react-hot-toast';
import { notify } from '../../../utils/notify';
import { translatePersonTypeToPortuguese, getBlockLabel, getUnitLabel } from '../../../utils/terminology';
import { departmentRelationLabel, departmentRelationTone } from './departmentRelation';
import { RoleUnitField } from './RoleUnitField';
import { CPFDisplay } from '../../CPFDisplay';
import { useCPFDisplay } from '../../../hooks/useCPFDisplay';
import { cn } from '../../../utils/cn';
import { CnhValidadeHint, pickCnhHintValue } from '../../registration/CnhValidadeHint';
import { isCnhValidadeField } from '../../../utils/cnhValidade';
import { LicensePlate } from '../../ui/LicensePlate';
import { VehicleDetailModal } from '../../OperationalView/components/VehicleDetailModal';
import { VehicleFormModal } from '../../OperationalView/components/VehicleFormModal';
import { DepartmentManager } from '../../DepartmentManager';
import { UnitAssignmentManager } from '../../UnitAssignmentManager';
import UnitSelector, { UnitOption } from '../../UnitSelector';
import { usePersonDetailContext } from '../../../contexts/PersonDetailContext';
import { useKeycloak } from '../../../contexts/KeycloakContext';
import { useCustomer } from '../../../contexts/CustomerContext';
import { hasMinimumRoleLevel } from '../../../config/roles';
import { useForceUppercase } from '../../../hooks/useForceUppercase';
import { PersonNoteBanner } from '../../PersonNoteBanner';
import { UserLinkingSection } from '../../registration/UserLinkingSection';
import { getParentescoLabel } from '../../registration/ParentescoField';
import { useNavigate } from 'react-router-dom';
import { formatCalendarDateToBR } from '../../../utils/dateFormatter';
import { syncVehicleRfid } from '../../../utils/vehicleRfidSync';
import { foreignerPayload, normalizePassport, countryOptions, countryName } from '../../../utils/foreigner';
import {
  effectivePersonRoles,
  canEditCPF as canEditCPFRule,
  showsCpfBypassHint,
  canDeletePerson,
  canManageUserLinking,
  canRevokeRole,
  liberacaoKindLabel,
  rolesSectionTitle,
  isDuplicateValue,
  roleAuthorizedByLink,
  roleAllowedDays,
  isSingleEntryRole,
  roleValidityLabel,
  roleStatusLabel,
  roleStatusTone,
} from '../personModalRules';

// WhatsApp helper function
const formatPhoneForWhatsApp = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11) {
    return `55${digits}`;
  } else if (digits.length === 10) {
    return `55${digits}`;
  }
  return digits;
};

interface UnitAssignment {
  unit_id: string;
  unit_number?: string;
  block_name?: string;
  street_name?: string;
  street_number?: string;
  relation_type: string;
  /** Optional "grau de parentesco" — a free-form kinship note, shown for any relation type. */
  parentesco?: string;
}

interface Department {
  id: string;
  name: string;
  time_zone_id?: string;
}

interface KeycloakUser {
  id: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

interface OverviewTabProps {
  person: PersonDetailData;
  onRefresh: () => void;
  readOnly?: boolean;
  darkMode?: boolean;
  isEditing?: boolean; // External control of edit mode (from PersonProfile)
  onEditingChange?: (editing: boolean) => void; // Callback to notify parent of edit mode changes
  onViewAllNotes?: () => void; // Callback to switch to notes tab
  onShowHistory?: () => void; // Callback to open modification history modal
  /**
   * End ONE clearance, from the card that shows it.
   *
   * Carries the role: a person holds several at once (Fernando Diniz holds three — Lote 185 twice
   * and Lote 137 once) and revoking by person would end whichever the backend found first.
   * Absent means this surface cannot revoke, and then nothing is offered.
   */
  onRevokeRole?: (role: any) => void;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ person, onRefresh, readOnly = false, darkMode = false, isEditing: externalIsEditing, onEditingChange, onViewAllNotes, onShowHistory, onRevokeRole }) => {
  const { closePersonDetail, onPersonUpdate, onDepartmentClick, openPersonDetail, onUnitClick } = usePersonDetailContext();
  const { roles: globalRoles, getRolesForCustomer, isSuperAdmin } = useKeycloak();
  const { selectedCustomer } = useCustomer();

  // Resolve customer ID: prefer person.customer?.id (always available), fall back to selectedCustomer
  const customerId = person.customer?.id || selectedCustomer?.id;

  // Get customer-specific roles (not global roles)
  const roles = customerId ? getRolesForCustomer(customerId) : globalRoles;

  // Gates live in ../personModalRules so they can be asserted — see its spec.
  const hasDeletePermission = canDeletePerson(roles);
  const effectiveRoles = effectivePersonRoles(roles, globalRoles);
  const [porteiroCPFEnabled, setPorteiroCPFEnabled] = useState(false);
  useEffect(() => {
    if (!canViewFullCPF(effectiveRoles) && customerId) {
      // Fetch settings to check if porteiro can edit CPF
      const tryEndpoints = async () => {
        for (const url of [
          `/api/v1/customers/${customerId}/settings`,
          `/api/v1/customers/${customerId}`,
        ]) {
          try {
            const r = await fetchWithAuth(url);
            if (r.ok) {
              const data = await r.json();
              if (data?.porteiro_can_edit_cpf !== undefined) {
                setPorteiroCPFEnabled(!!data.porteiro_can_edit_cpf);
                return;
              }
            }
          } catch (e) {}
        }
      };
      tryEndpoints();
    }
  }, [customerId]);
  const canEditCPF = canEditCPFRule(effectiveRoles, porteiroCPFEnabled);
  // One CPF-visibility answer for the whole tab, scoped to this person's customer.
  const cpfDisplay = useCPFDisplay(customerId);
  // Porteiro can edit CPF but cannot see the current value
  const [cpfEditActive, setCpfEditActive] = useState(false); // whether porteiro clicked "Alterar CPF"
  const [cpfDraft, setCpfDraft] = useState(''); // new CPF value being typed
  // Only admins (90+) can manage Keycloak user linking — others don't see this section at all
  const canManageUsers = canManageUserLinking(roles);
  const forceUpper = useForceUppercase();

  const [localIsEditing, setLocalIsEditing] = useState(false);

  // Use external isEditing if provided, otherwise use local state
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : localIsEditing;

  // Full name field (combines first_name + last_name for editing)
  const [fullNameInput, setFullNameInput] = useState(() => joinFullName(person.first_name, person.last_name));
  const [fullNameError, setFullNameError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isSavingHistoryPrivacy, setIsSavingHistoryPrivacy] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSecurityRiskConfirm, setShowSecurityRiskConfirm] = useState(false);
  const [revokeFailCount, setRevokeFailCount] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [timeZones, setTimeZones] = useState<TimeZone[]>([]);
  const [viewingDocumentImage, setViewingDocumentImage] = useState<{ url: string; title: string } | null>(null);
  const [vehicleDetail, setVehicleDetail] = useState<any>(null);
  const [editingVehicle, setEditingVehicle] = useState<any>(null);
  const [syncingVehicleId, setSyncingVehicleId] = useState<string | null>(null);
  const [revokingVehicleId, setRevokingVehicleId] = useState<string | null>(null);

  /** Mirrors VehiclesTab.handleDeleteVehicle, confirmation included. */
  const deleteVehicle = async (vehicle: any) => {
    const vehicleCustomerId = person.customer?.id;
    if (!vehicleCustomerId) return;
    if (!window.confirm(`Deseja realmente excluir o veículo ${vehicle.plate}?`)) return;
    try {
      const res = await fetchWithAuth(
        `/api/v1/customers/${vehicleCustomerId}/vehicles/${vehicle.id}`,
        { method: 'DELETE' },
      );
      if (res.ok) {
        toast.success('Veículo excluído!');
        onRefresh();
      } else {
        toast.error('Erro ao excluir veículo');
      }
    } catch {
      toast.error('Erro ao excluir veículo');
    }
  };

  /** Mirrors VehiclesTab.syncOrRevoke so both entry points behave identically. */
  const syncOrRevokeVehicle = async (vehicle: any, isAllowed: boolean) => {
    const vehicleCustomerId = person.customer?.id;
    if (!vehicleCustomerId) return;
    const setBusy = isAllowed ? setSyncingVehicleId : setRevokingVehicleId;
    setBusy(vehicle.id);
    try {
      await syncVehicleRfid({ customerId: vehicleCustomerId, vehicle, isAllowed });
    } finally {
      setBusy(null);
    }
  };

  // Tracks if UnitAssignmentManager has a unit selected but not yet "Adicionar"ed
  const [unitManagerHasPending, setUnitManagerHasPending] = useState(false);

  // Unit detail expansion
  const [expandedUnitId, setExpandedUnitId] = useState<string | null>(null);
  const [unitResidents, setUnitResidents] = useState<any[]>([]);
  const [unitResidentsLoading, setUnitResidentsLoading] = useState(false);
  const [unitResidentsPage, setUnitResidentsPage] = useState(0);
  const UNIT_PAGE_SIZE = 5;

  const fetchUnitResidents = useCallback(async (unitId: string) => {
    setUnitResidentsLoading(true);
    setUnitResidentsPage(0);
    try {
      const res = await fetchWithAuth(`/api/v1/units/${unitId}/residents`);
      if (res.ok) {
        const data = await res.json();
        const residents: any[] = (Array.isArray(data) ? data : (data.residents || data.data || []))
          .filter((r: any) => r.id !== person.id);

        // Residents endpoint doesn't include photos — enrich if needed
        const hasPhotos = residents.length === 0 || residents[0].profile_picture_url || residents[0].identity_photo_url;
        if (!hasPhotos) {
          const enriched = await Promise.all(
            residents.map(async (r) => {
              try {
                const fullRes = await fetchWithAuth(`/api/v1/people/${r.id}/full`);
                if (fullRes.ok) {
                  const full = await fullRes.json();
                  return { ...r, profile_picture_url: full.profile_picture_url, identity_photo_url: full.identity_photo_url };
                }
              } catch { /* keep original */ }
              return r;
            })
          );
          setUnitResidents(enriched);
        } else {
          setUnitResidents(residents);
        }
      }
    } catch {
      setUnitResidents([]);
    } finally {
      setUnitResidentsLoading(false);
    }
  }, [person.id]);
  const [selectedUser, setSelectedUser] = useState<KeycloakUser | null>(null);
  const [loadingLinkedUser, setLoadingLinkedUser] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [generatingInvite, setGeneratingInvite] = useState(false);
  const navigate = useNavigate();

  // Determine person type from active role for field config lookup
  const activeRole = person.roles?.find(r => r.is_active);
  const personType = activeRole?.role_type ?? 'visitor';
  const fieldConfig = usePersonFieldConfig(person.customer?.id, personType);

  // Same order + width the admin configured in Campos do Cadastro — applied via CSS `order`
  // so each field's existing (often stateful) markup can stay exactly where it is in the JSX
  // source; only the visual position and col-span class move.
  const orderedFields = useMemo(() => getOrderedFields(fieldConfig ?? {}), [fieldConfig]);
  const fieldOrder = useCallback(
    (key: string) => {
      const idx = orderedFields.findIndex(i => i.key === key && !i.isCustom);
      return idx === -1 ? undefined : idx;
    },
    [orderedFields],
  );
  const fieldColSpanClass = useCallback(
    (key: string) => {
      const item = orderedFields.find(i => i.key === key && !i.isCustom);
      return item?.col_span === 2 ? 'col-span-2' : undefined;
    },
    [orderedFields],
  );

  // Get the active staff role for department editing
  const activeStaffRole = person.roles?.find(r => r.is_active && r.role_type === 'staff');
  const metadata = activeStaffRole?.role_metadata;

  // Position may be stored on a different staff role than the first one (e.g. when departments
  // are added as separate roles). Scan all active staff roles for the one carrying the position.
  const activeStaffRoles = person.roles?.filter(r => r.is_active && r.role_type === 'staff') || [];
  const staffRoleWithPosition = activeStaffRoles.find(r => r.role_metadata?.position) ?? activeStaffRole;
  const currentPosition = staffRoleWithPosition?.role_metadata?.position || '';

  // Support both new (multiple departments) and legacy (single department) formats
  const currentDepartmentIds = metadata?.department_ids ||
                                (metadata?.department_id ? [metadata.department_id] : []);

  /**
   * Relation per department. Each department membership is its own staff PersonRole server-side
   * (role_metadata.department_id + relation_type), so the relations are read off those roles
   * rather than from a parallel array — which is also why two departments can hold two different
   * relations for the same person.
   */
  const currentDepartmentRelations: Record<string, string> = React.useMemo(() => {
    const out: Record<string, string> = {};
    (person.roles || []).forEach((role: any) => {
      if (role.role_type !== 'staff' || !role.is_active) return;
      const deptId = role.role_metadata?.department_id;
      const relation = role.role_metadata?.relation_type;
      if (deptId && relation) out[deptId] = relation;
    });
    return out;
  }, [person.roles]);

  // Get the active resident role for unit editing
  const activeResidentRole = person.roles?.find(r => r.is_active && r.role_type === 'resident');
  const residentMetadata = activeResidentRole?.role_metadata;

  // Get the active condo employee ("funcionário") role for unit editing — same idea as
  // residents (single unit, no ownership relation_type concept).
  const activeCondoEmployeeRole = person.roles?.find(r => r.is_active && r.role_type === 'condo_employee');
  const condoEmployeeMetadata = activeCondoEmployeeRole?.role_metadata;

  // Condo employee ("funcionário") single unit assignment — edit-mode local state
  const [employeeUnit, setEmployeeUnit] = useState<UnitOption | null>(
    condoEmployeeMetadata?.unit_id ? {
      value: condoEmployeeMetadata.unit_id,
      label: [condoEmployeeMetadata.block_name, condoEmployeeMetadata.unit_number].filter(Boolean).join(' - '),
      unitNumber: condoEmployeeMetadata.unit_number,
      blockName: condoEmployeeMetadata.block_name,
    } : null
  );

  // Get the active visitor role for intercom editing
  const activeVisitorRole = person.roles?.find(r => r.is_active && r.role_type === 'visitor');

  // Support both new (unit_assignments) and legacy (unit_ids + relation_type) formats
  const currentUnitAssignments: UnitAssignment[] = residentMetadata?.unit_assignments ||
    (residentMetadata?.unit_ids || (residentMetadata?.unit_id ? [residentMetadata.unit_id] : [])).map((id: string) => ({
      unit_id: id,
      unit_number: residentMetadata?.unit_number,
      block_name: residentMetadata?.block_name,
      relation_type: residentMetadata?.relation_type || 'proprietario',
      parentesco: residentMetadata?.parentesco,
    }));

  const [formData, setFormData] = useState({
    first_name: person.first_name,
    last_name: person.last_name,
    email: person.email || '',
    phone: person.phone || '',
    cpf: formatCPF(person.cpf || person.document_no || ''),
    date_of_birth: person.date_of_birth || '',
    is_minor: person.is_adult === false, // only true when explicitly marked as minor
    department_ids: currentDepartmentIds,
    department_relations: currentDepartmentRelations,
    position: currentPosition,
    unit_assignments: currentUnitAssignments,
    keycloak_user_id: person.keycloak_user_id || null,
    requires_intercom: activeVisitorRole?.role_metadata?.requires_intercom ?? true,
    extra_data: (person.extra_data ?? {}) as Record<string, any>,
    rg: person.rg || '',
    is_foreigner: !!person.is_foreigner,
    passport_number: person.passport_number || '',
    country: person.country || '',
    time_zone_id: person.time_zone_id || '',
  });

  // Helper function to calculate age from birth date
  const calculateAge = (birthDate: string | undefined): number | null => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Format date for display (dd/mm/yyyy)
  // Calendar dates (date_of_birth) are formatted by utils/dateFormatter's
  // formatCalendarDate* helpers — the private copies here applied a timezone shift that
  // rendered every birth date one day early in Brazil.

  // Fetch departments when editing and customer is empresa, OR (view mode) when we need
  // to resolve an inherited Grade de Horário for the live status indicator below.
  const timeZonesEnabled = !!(
    person.customer?.condo_data?.time_zones_enabled ||
    person.customer?.empresa_data?.time_zones_enabled
  );
  useEffect(() => {
    const needsForInheritance = timeZonesEnabled && !person.time_zone_id && currentDepartmentIds.length > 0;
    if ((isEditing || needsForInheritance) && person.customer?.type === 'empresa' && (activeStaffRole || needsForInheritance)) {
      fetchDepartments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, person.customer?.type, person.customer?.id, timeZonesEnabled, person.time_zone_id]);

  // Fetch time zones (full schedule, not just id/name) whenever Grade de Horário is
  // enabled — needed in view mode too, to show a live "within schedule now?" status.
  useEffect(() => {
    if (timeZonesEnabled && person.customer?.id) {
      condoApi.listTimeZones(person.customer.id).then((data: any) => {
        const list = data?.time_zones ?? (Array.isArray(data) ? data : []);
        setTimeZones(list);
      }).catch(() => setTimeZones([]));
    }
  }, [isEditing, timeZonesEnabled, person.customer?.id]);

  // Fetch linked Keycloak user if exists (only in edit mode or for admins)
  useEffect(() => {
    const fetchLinkedUser = async () => {
      if (person.keycloak_user_id && isEditing && canManageUsers) {
        setLoadingLinkedUser(true);
        try {
          const response = await api.get(`/keycloak/users/${person.keycloak_user_id}`, { _silentOnError: true } as any);
          if (response.data?.data) {
            setSelectedUser({
              id: response.data.data.id,
              username: response.data.data.username,
              email: response.data.data.email,
              firstName: response.data.data.firstName,
              lastName: response.data.data.lastName,
            });
          }
        } catch (err) {
          console.error('Failed to fetch linked user:', err);
          // If 403 or user not found, just keep selectedUser as null
          // We'll show a simple "Conta Vinculada" message instead
          setSelectedUser(null);
        } finally {
          setLoadingLinkedUser(false);
        }
      }
    };

    fetchLinkedUser();
  }, [person.keycloak_user_id, isEditing]);

  const fetchDepartments = async () => {
    if (!person.customer?.id) return;

    setLoadingDepartments(true);
    try {
      const response = await api.get(`/customers/${person.customer.id}/departments`);
      // API returns { data: departments }
      const depts = response.data?.data || response.data || [];
      setDepartments(Array.isArray(depts) ? depts : []);
    } catch (err) {
      console.error('Failed to fetch departments:', err);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const handleSave = async () => {
    console.log('[OverviewTab] handleSave called', {
      activeResidentRole: activeResidentRole?.id,
      currentUnitAssignments,
      formDataUnitAssignments: formData.unit_assignments
    });

    const nameError = validateFullName(fullNameInput);
    if (nameError) {
      setFullNameError(nameError);
      return;
    }
    const { first_name, last_name } = splitFullName(fullNameInput);

    // Enforce "required" fields as configured in Campos do Cadastro — same set of fields
    // the "*" labels above reflect.
    if (fieldConfig?.standard_fields?.email?.required && !formData.email?.trim()) {
      toast.error('E-mail é obrigatório');
      return;
    }
    if (fieldConfig?.standard_fields?.phone?.required && !formData.phone?.trim()) {
      toast.error('Telefone é obrigatório');
      return;
    }
    if (fieldConfig?.standard_fields?.date_of_birth?.required && !formData.date_of_birth) {
      toast.error('Data de Nascimento é obrigatória');
      return;
    }
    if (fieldConfig?.standard_fields?.rg?.required && !formData.rg?.trim()) {
      toast.error('RG é obrigatório');
      return;
    }
    for (const field of fieldConfig?.custom_fields ?? []) {
      if (field.required && !formData.extra_data?.[field.id]) {
        toast.error(`${field.label} é obrigatório`);
        return;
      }
    }

    setSaving(true);
    try {
      // Step 1: Update person basic info
      // See cpfToSubmit — three controls, one value, and the only place clearing is decided.
      const nextCpf = cpfToSubmit({
        canEdit: canEditCPF,
        canViewFull: canViewFullCPF(effectiveRoles),
        formCpf: formData.cpf,
        draftActive: cpfEditActive,
        draft: cpfDraft,
        storedCpf: person.cpf || person.document_no,
      });

      const personData: any = {
        first_name,
        last_name,
        email: formData.email,
        phone: formData.phone,
        keycloak_user_id: selectedUser?.id || null,
        extra_data: formData.extra_data,
        rg: formData.rg || null,
        ...foreignerPayload(formData),
        time_zone_id: formData.time_zone_id || '',
      };

      // Send `cpf` ONLY when it actually changed. The backend validates and duplicate-checks
      // the CPF whenever the field is present, and skips it entirely when absent — so sending
      // an untouched CPF made people whose CPF is stored in the old formatted style
      // ("320.179.328-09", from CreateEmployee, which never normalized) impossible to save:
      // the backend normalizes the incoming value, sees it differ from the stored text, and
      // reports "CPF já cadastrado" against the other row holding the same CPF normalized.
      // See shouldSendCpfOnUpdate for the digit-only comparison and the masked-value case.
      if (shouldSendCpfOnUpdate(nextCpf, person.cpf || person.document_no || '')) {
        personData.cpf = nextCpf;
      }

      // Only include date_of_birth if provided
      if (formData.date_of_birth) {
        personData.date_of_birth = new Date(formData.date_of_birth).toISOString();
      }

      // Include is_adult flag (only meaningful when no birth date)
      // Convert is_minor to is_adult: is_minor=true means is_adult=false
      personData.is_adult = !formData.is_minor;

      await api.put(`/customers/${person.customer?.id}/people/${person.id}`, personData);

      // Step 2: PATCH departments/position if staff role changed
      if (activeStaffRole) {
        const currentIds = currentDepartmentIds.sort();
        const newIds = formData.department_ids.sort();
        const newPosition = formData.position;

        const depsChanged = JSON.stringify(currentIds) !== JSON.stringify(newIds);
        const relationsChanged = JSON.stringify(currentDepartmentRelations) !== JSON.stringify(formData.department_relations);
        const positionChanged = currentPosition !== newPosition;

        if (depsChanged || relationsChanged || positionChanged) {
          const patch: Record<string, any> = {};
          // department_assignments carries the membership AND the relation of each department in
          // one payload; the backend does the same create/keep/deactivate work as department_ids
          // and then stamps each department's own relation.
          if (depsChanged || relationsChanged) {
            patch.department_assignments = formData.department_ids.map((id: string) => ({
              department_id: id,
              relation_type: formData.department_relations?.[id] || '',
            }));
          }
          if (positionChanged) patch.position = newPosition;

          // Patch to the role that already carries the position (or fall back to first staff role)
          const roleIdForPatch = (staffRoleWithPosition ?? activeStaffRole).id;
          await api.patch(
            `/customers/${person.customer?.id}/people/${person.id}/roles/${roleIdForPatch}/metadata`,
            patch
          );
          if (depsChanged) {
            toast.success(
              `${formData.department_ids.length} departamento${formData.department_ids.length > 1 ? 's' : ''} atualizado${formData.department_ids.length > 1 ? 's' : ''} com sucesso`
            );
          }
          if (positionChanged) {
            toast.success('Cargo atualizado com sucesso');
          }
        }
      }

      // Step 3: PATCH unit assignments if resident role changed
      if (activeResidentRole) {
        const currentAssignments = JSON.stringify(currentUnitAssignments.sort((a, b) => a.unit_id.localeCompare(b.unit_id)));
        const newAssignments = JSON.stringify(formData.unit_assignments.sort((a, b) => a.unit_id.localeCompare(b.unit_id)));

        console.log('[OverviewTab] Unit assignments comparison:', {
          currentAssignments,
          newAssignments,
          areEqual: currentAssignments === newAssignments
        });

        if (currentAssignments !== newAssignments) {
          console.log('[OverviewTab] Saving unit assignments:', formData.unit_assignments);
          await api.patch(
            `/customers/${person.customer?.id}/people/${person.id}/roles/${activeResidentRole.id}/metadata`,
            { unit_assignments: formData.unit_assignments }
          );
          toast.success(
            `${formData.unit_assignments.length} unidade${formData.unit_assignments.length > 1 ? 's' : ''} atualizada${formData.unit_assignments.length > 1 ? 's' : ''} com sucesso`
          );
        } else {
          console.log('[OverviewTab] No unit assignment changes detected');
        }
      }

      // Step 3b: PATCH unit assignment if condo employee role changed (single unit, no relation_type)
      if (activeCondoEmployeeRole) {
        const currentEmployeeUnitId = condoEmployeeMetadata?.unit_id || '';
        const newEmployeeUnitId = employeeUnit?.value || '';

        if (currentEmployeeUnitId !== newEmployeeUnitId) {
          // PatchPersonRoleMetadata only understands unit_ids (legacy single-relation-type
          // format) — a bare unit_id/unit_number/block_name payload is silently ignored.
          await api.patch(
            `/customers/${person.customer?.id}/people/${person.id}/roles/${activeCondoEmployeeRole.id}/metadata`,
            { unit_ids: newEmployeeUnitId ? [newEmployeeUnitId] : [] }
          );
          toast.success(newEmployeeUnitId ? 'Unidade atualizada com sucesso' : 'Unidade removida com sucesso');
        }
      }

      // Step 4: PATCH intercom setting if visitor role changed
      if (activeVisitorRole) {
        const currentRequiresIntercom = activeVisitorRole.role_metadata?.requires_intercom ?? true;
        const newRequiresIntercom = formData.requires_intercom;

        if (currentRequiresIntercom !== newRequiresIntercom) {
          await api.patch(
            `/customers/${person.customer?.id}/people/${person.id}/roles/${activeVisitorRole.id}/metadata`,
            { requires_intercom: formData.requires_intercom }
          );
          toast.success('Configuração de interfone atualizada com sucesso');
        }
      }

      setCpfDraft('');
      setCpfEditActive(false);
      // Exit edit mode - use callback if externally controlled, otherwise use local state
      if (externalIsEditing !== undefined && onEditingChange) {
        onEditingChange(false);
      } else {
        setLocalIsEditing(false);
      }
      onRefresh();

      // Notify parent to refresh data after successful save
      if (onPersonUpdate) {
        onPersonUpdate(person.id);
      }
    } catch (err: any) {
      console.error('Failed to update person:', err);
      toast.error(err.response?.data?.error || 'Erro ao atualizar pessoa');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFullNameInput(joinFullName(person.first_name, person.last_name));
    setFullNameError(null);
    setFormData({
      first_name: person.first_name,
      last_name: person.last_name,
      email: person.email || '',
      phone: person.phone || '',
      cpf: formatCPF(person.cpf || person.document_no || ''),
      date_of_birth: person.date_of_birth || '',
      is_minor: person.is_adult === false, // only true when explicitly marked as minor
      department_ids: currentDepartmentIds,
      department_relations: currentDepartmentRelations,
      position: currentPosition,
      unit_assignments: currentUnitAssignments,
      keycloak_user_id: person.keycloak_user_id || null,
      requires_intercom: activeVisitorRole?.role_metadata?.requires_intercom ?? true,
      extra_data: (person.extra_data ?? {}) as Record<string, any>,
      rg: person.rg || '',
      is_foreigner: !!person.is_foreigner,
      passport_number: person.passport_number || '',
      country: person.country || '',
      time_zone_id: person.time_zone_id || '',
    });
    // Reset selected user to original (will be re-fetched by useEffect)
    if (person.keycloak_user_id) {
      // Re-trigger fetch by temporarily clearing selectedUser
      setSelectedUser(null);
      // The useEffect will re-fetch the linked user
    }
    setCpfDraft('');
    setCpfEditActive(false);
    // Exit edit mode - use callback if externally controlled, otherwise use local state
    if (externalIsEditing !== undefined && onEditingChange) {
      onEditingChange(false);
    } else {
      setLocalIsEditing(false);
    }
  };

  const executeRevokeAllBeforeDelete = async (): Promise<{ successCount: number; failedCount: number }> => {
    let zones: any[] = [];
    try {
      const response = await api.get(`/customers/${person.customer.id}/people/${person.id}/effective-access-zones`);
      zones = Array.isArray(response.data?.data) ? response.data.data
            : Array.isArray(response.data) ? response.data
            : [];
    } catch (err) {
      console.warn('[DeleteRevoke] Failed to fetch zones:', err);
      return { successCount: 0, failedCount: 0 };
    }

    const seenDeviceIds = new Set<string>();
    const allDevices: { id: string; name: string }[] = [];
    zones.forEach((zone: any) => {
      zone.devices?.forEach((device: any) => {
        if (!seenDeviceIds.has(device.id)) {
          seenDeviceIds.add(device.id);
          allDevices.push({ id: device.id, name: device.name });
        }
      });
    });

    if (allDevices.length === 0) return { successCount: 0, failedCount: 0 };

    const isElectronApp = typeof window !== 'undefined' && !!(window as any).electronAPI;
    const deviceDetailsMap = new Map<string, DeviceInfo>();

    if (isElectronApp) {
      const results = await Promise.allSettled(
        allDevices.map(d => api.get(`/iot-devices/${d.id}`).then(r => r.data as DeviceInfo).catch(() => null))
      );
      results.forEach((r, i) => {
        if (r.status === 'fulfilled' && r.value) deviceDetailsMap.set(allDevices[i].id, r.value);
      });
    }

    let successCount = 0;
    let failedCount = 0;
    let processed = 0;
    const toastId = notify.loading('Revogando acesso', `0 de ${allDevices.length} dispositivos`);

    for (const device of allDevices) {
      const deviceStart = Date.now();
      try {
        const deviceInfo = deviceDetailsMap.get(device.id);
        if (isElectronApp && deviceInfo && (deviceInfo.local_ip_address || deviceInfo.ip_address)) {
          try {
                await DeviceIntegration.instance.execute(deviceInfo, DeviceOperations.REMOVE_PERSON, { person_id: person.id }, CommunicationMode.HYBRID);
          } catch (directErr) {
            console.warn(`[DeleteRevoke] HYBRID failed for ${device.name}:`, directErr);
          }
        }
        if (isElectronApp) {
          await api.patch(`/customers/${person.customer!.id}/people/${person.id}/devices/${device.id}/mark-revoked`);
        } else {
          await api.delete(`/customers/${person.customer!.id}/people/${person.id}/devices/${device.id}/revoke`);
        }
        successCount++;
      } catch (err) {
        console.warn(`[DeleteRevoke] Failed for ${device.name}:`, err);
        failedCount++;
      }
      processed++;
      notify.loading('Revogando acesso', `${processed} de ${allDevices.length} dispositivos`, { id: toastId });
      const elapsed = Date.now() - deviceStart;
      if (elapsed < 500) await new Promise(r => setTimeout(r, 500 - elapsed));
    }

    toast.dismiss(toastId);
    return { successCount, failedCount };
  };

  const handleGenerateInvite = async () => {
    setGeneratingInvite(true);
    try {
      const response = await api.post(
        `/customers/${person.customer.id}/people/${person.id}/invite`,
        {}
      );
      const url = response.data?.invite_url as string;
      setInviteUrl(url);
      // Copy to clipboard
      try { await navigator.clipboard.writeText(url); toast.success('Link copiado!'); } catch {}
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Erro ao gerar link';
      toast.error(msg);
    } finally {
      setGeneratingInvite(false);
    }
  };

  const handleDeleteWithRevoke = async () => {
    setShowDeleteConfirm(false);
    const { failedCount } = await executeRevokeAllBeforeDelete();
    if (failedCount > 0) {
      setRevokeFailCount(failedCount);
      setShowSecurityRiskConfirm(true);
    } else {
      await handleDelete();
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/customers/${person.customer.id}/people/${person.id}`);
      toast.success('Pessoa excluída com sucesso');

      // Close the modal (do NOT call onPersonUpdate — that triggers fetchPersonDetails
      // on the now-deleted person, which shows a spurious 404 error toast)
      closePersonDetail();
    } catch (err: any) {
      console.error('Failed to delete person:', err);

      // Check for permission error
      const errorMessage = err.response?.data?.error || '';
      if (errorMessage.includes('Insufficient permissions') || errorMessage.includes('Required one of')) {
        toast.error('Permissões insuficientes. Somente administradores do sistema podem excluir pessoas. Entre em contato com um administrador.');
      } else {
        toast.error(errorMessage || 'Falha ao excluir pessoa');
      }
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };


  // Grade de Horário — live "is this person within their allowed schedule right now?"
  // status. Resolves the effective schedule (own assignment, else inherited from
  // department, else none = unrestricted) and re-evaluates every 30s while the modal
  // is open so the badge below stays accurate without needing a manual refresh.
  const [scheduleNowTick, setScheduleNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!timeZonesEnabled) return;
    const id = setInterval(() => setScheduleNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, [timeZonesEnabled]);

  // Visitors/prestadores can inherit the schedule of the resident who authorized
  // their visit instead of (or before falling back to) the department default —
  // enabled per-customer via "Herança de Grade de Horário" in Configurações.
  // The actual enforcement always happens server-side (condo-manager resolves
  // it live for the Hikvision middleware); here we only detect that inheritance
  // applies so we don't show a misleading "Sem restrição" status.
  const visitorScheduleInheritEnabled = !!(
    (person.customer as any)?.condo_data?.visitor_schedule_inherit_enabled ||
    (person.customer as any)?.empresa_data?.visitor_schedule_inherit_enabled
  );
  const responsiblePersonRole = person.roles?.find(role =>
    (role.role_type === 'visitor' || role.role_type === 'service_provider') &&
    !!(role.role_metadata?.authorized_by_resident_id || role.role_metadata?.visiting_resident_id || role.role_metadata?.responsible_person_id)
  );
  const inheritsFromResponsiblePerson = !person.time_zone_id && visitorScheduleInheritEnabled && !!responsiblePersonRole;

  const inheritedDepartment = !person.time_zone_id && !inheritsFromResponsiblePerson
    ? departments.find(d => currentDepartmentIds.includes(d.id) && d.time_zone_id)
    : undefined;
  const effectiveTimeZoneId = person.time_zone_id || inheritedDepartment?.time_zone_id;
  const effectiveTimeZone = effectiveTimeZoneId
    ? timeZones.find(tz => tz.id === effectiveTimeZoneId)
    : undefined;

  const scheduleStatus = (() => {
    if (!effectiveTimeZone) return null;
    const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const now = new Date(scheduleNowTick);
    const today = effectiveTimeZone.schedule?.[dayKeys[now.getDay()]];
    if (!today?.enabled || !today.start || !today.end) {
      return { within: false, label: 'Fora do horário agora (sem expediente hoje)' };
    }
    const toMinutes = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const within = nowMin >= toMinutes(today.start) && nowMin <= toMinutes(today.end);
    return {
      within,
      label: within
        ? `Dentro do horário agora · até ${today.end}`
        : `Fora do horário agora · ${today.start}–${today.end}`,
    };
  })();

  // Document photo URLs (identity_photos with photo_type prefix 'document', fallback to legacy fields)
  const documentPhotos = person.identity_photos?.filter(p => p.photo_type?.startsWith('document')) || [];
  const documentFrontUrl = documentPhotos.find(p => p.photo_type === 'document_front')?.photo_url || person.document_front_url;
  const documentBackUrl = documentPhotos.find(p => p.photo_type === 'document_back')?.photo_url || person.document_back_url;

  return (
    <div className="space-y-6">
      {/* Person Note Banner - Show latest note */}
      {person.customer?.id && (
        <PersonNoteBanner
          personId={person.id}
          customerId={person.customer.id}
          darkMode={darkMode}
          onViewAllNotes={onViewAllNotes}
        />
      )}

      {/* Personal Information */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className={cn(
            "text-xs font-semibold uppercase tracking-widest",
            darkMode ? "text-gray-400" : "text-gray-500"
          )}>
            Informações Pessoais
          </h3>
          {!readOnly && (
            !isEditing ? (
              <button
                onClick={() => {
                  if (externalIsEditing !== undefined && onEditingChange) {
                    onEditingChange(true);
                  } else {
                    setLocalIsEditing(true);
                  }
                }}
                className={cn(
                  "text-xs font-medium px-2.5 py-1 rounded-md transition-colors",
                  darkMode ? "text-blue-400 hover:bg-blue-900/30" : "text-blue-600 hover:bg-blue-50"
                )}
              >
                Editar
              </button>
            ) : (
              <div className="flex items-center gap-1">
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className={cn(
                    "text-xs font-medium px-2.5 py-1 rounded-md transition-colors disabled:opacity-50",
                    darkMode ? "text-gray-400 hover:bg-gray-700" : "text-gray-500 hover:bg-gray-100"
                  )}
                >
                  Cancelar
                </button>
                <div className="flex items-center gap-2">
                  {unitManagerHasPending && (
                    <span className="text-[11px] text-amber-600 font-medium flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Clique em "Adicionar" para confirmar a unidade
                    </span>
                  )}
                  <button
                    onClick={unitManagerHasPending ? undefined : handleSave}
                    disabled={saving || unitManagerHasPending}
                    title={unitManagerHasPending ? 'Confirme a unidade clicando em "Adicionar" antes de salvar' : undefined}
                    className={cn(
                      "text-xs font-medium px-2.5 py-1 rounded-md transition-colors",
                      unitManagerHasPending
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    )}
                  >
                    {saving ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            )
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2" style={{ order: -1 }}>
            <label className={cn(
              "block text-sm font-medium mb-1",
              darkMode ? "text-gray-300" : "text-gray-700"
            )}>
              Nome Completo<span className="text-red-500"> *</span>
            </label>
            {isEditing ? (
              <div>
                <input
                  type="text"
                  value={fullNameInput}
                  style={forceUpper ? { textTransform: 'uppercase' } : undefined}
                  onChange={(e) => {
                    const name = sanitizePersonName(e.target.value);
                    setFullNameInput(forceUpper ? name.toUpperCase() : name);
                    setFullNameError(null);
                  }}
                  placeholder="Nome e Sobrenome"
                  className={cn(
                    "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                    fullNameError
                      ? "border-red-400 focus:ring-red-400"
                      : darkMode ? "border-gray-600" : "border-gray-300",
                    darkMode ? "bg-gray-800 text-gray-100" : "bg-white text-gray-900"
                  )}
                />
                {fullNameError && (
                  <p className="mt-1 text-xs text-red-500">{fullNameError}</p>
                )}
              </div>
            ) : (
              <p className={cn(darkMode ? "text-gray-100" : "text-gray-900")}>
                {joinFullName(person.first_name, person.last_name)}
              </p>
            )}
          </div>

          {(fieldConfig?.standard_fields?.email?.enabled !== false || person.email) && (
          <div className={fieldColSpanClass('email')} style={{ order: fieldOrder('email') }}>
            <label className={cn(
              "block text-sm font-medium mb-1",
              darkMode ? "text-gray-300" : "text-gray-700"
            )}>
              E-mail{fieldConfig?.standard_fields?.email?.required && <span className="text-red-500"> *</span>}
            </label>
            {isEditing ? (
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={cn(
                  "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                  darkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"
                )}
              />
            ) : (
              <p className={cn(darkMode ? "text-gray-100" : "text-gray-900")}>{person.email || '-'}</p>
            )}
          </div>
          )}

          {(fieldConfig?.standard_fields?.phone?.enabled !== false || person.phone) && (
          <div className={fieldColSpanClass('phone')} style={{ order: fieldOrder('phone') }}>
            <label className={cn(
              "block text-sm font-medium mb-1",
              darkMode ? "text-gray-300" : "text-gray-700"
            )}>
              Telefone{fieldConfig?.standard_fields?.phone?.required && <span className="text-red-500"> *</span>}
            </label>
            {isEditing ? (
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
                  let masked = digits;
                  if (digits.length > 2) masked = `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
                  if (digits.length > 7) masked = `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
                  setFormData({ ...formData, phone: masked });
                }}
                className={cn(
                  "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                  darkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"
                )}
              />
            ) : (
              <div className="flex items-center gap-2">
                <p className={cn(darkMode ? "text-gray-100" : "text-gray-900")}>{person.phone || '-'}</p>
                {person.phone && person.phone.replace(/\D/g, '').length >= 10 && (
                  <button
                    type="button"
                    onClick={() => {
                      const formattedPhone = formatPhoneForWhatsApp(person.phone);
                      window.open(`https://wa.me/${formattedPhone}`, '_blank');
                    }}
                    className={cn(
                      "flex-shrink-0 px-2 py-1 rounded-md transition-colors flex items-center gap-1",
                      darkMode
                        ? "bg-green-600 hover:bg-green-700 text-white"
                        : "bg-green-500 hover:bg-green-600 text-white"
                    )}
                    title="Abrir WhatsApp"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>
          )}

          {/* Birth Date with Age */}
          <div className={fieldColSpanClass('date_of_birth')} style={{ order: fieldOrder('date_of_birth') }}>
            <label className={cn(
              "block text-sm font-medium mb-1",
              darkMode ? "text-gray-300" : "text-gray-700"
            )}>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                <span>Data de Nascimento{fieldConfig?.standard_fields?.date_of_birth?.required && <span className="text-red-500"> *</span>}</span>
              </div>
            </label>
            {isEditing ? (
              <div className="space-y-2">
                <DateInput
                  value={formData.date_of_birth}
                  onChange={(isoDate) => setFormData({ ...formData, date_of_birth: isoDate, is_minor: false })}
                  className={cn(
                    "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                    darkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"
                  )}
                />
                {/* Show calculated age when birth date is provided */}
                {formData.date_of_birth && (
                  <div className={cn(
                    "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium",
                    darkMode ? "bg-blue-900/20 text-blue-400" : "bg-blue-50 text-blue-700"
                  )}>
                    <Cake className="w-4 h-4" />
                    <span>{calculateAge(formData.date_of_birth)} anos</span>
                  </div>
                )}
                {/* Show is_minor checkbox when no birth date */}
                {!formData.date_of_birth && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_minor}
                      onChange={(e) => setFormData({ ...formData, is_minor: e.target.checked })}
                      className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <span className={cn(
                      "text-sm",
                      darkMode ? "text-gray-300" : "text-gray-700"
                    )}>
                      Menor de 18 anos
                    </span>
                  </label>
                )}
              </div>
            ) : (
              <div className="space-y-1">
                <p className={cn(darkMode ? "text-gray-100" : "text-gray-900")}>
                  {formatCalendarDateToBR(person.date_of_birth) || 'Não informada'}
                </p>
                {/* Display age badge in view mode */}
                {person.date_of_birth && (
                  <div className={cn(
                    "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium",
                    darkMode ? "bg-blue-900/20 text-blue-400" : "bg-blue-50 text-blue-700"
                  )}>
                    <Cake className="w-4 h-4" />
                    <span>{calculateAge(person.date_of_birth)} anos</span>
                  </div>
                )}
                {/* Display is_minor badge only when explicitly marked as minor */}
                {!person.date_of_birth && person.is_adult === false && (
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium">
                    Menor de 18 anos
                  </div>
                )}
              </div>
            )}
          </div>

          {/* CPF — shown when enabled in fieldConfig or person already has a value */}
          {/* A foreigner is identified by the passport below — the CPF row is not shown for
              one, the same way the cadastro stops asking for it. */}
          {!(isEditing ? formData.is_foreigner : person.is_foreigner) &&
            (fieldConfig?.standard_fields?.cpf?.enabled !== false || person.cpf || person.document_no) && (
          <div className={fieldColSpanClass('cpf')} style={{ order: fieldOrder('cpf') }}>
            <label className={cn(
              "block text-sm font-medium mb-1",
              darkMode ? "text-gray-300" : "text-gray-700"
            )}>
              CPF {(fieldConfig?.standard_fields?.cpf?.required !== false) && <span className="text-red-500">*</span>}
            </label>
            {isEditing ? (
              canEditCPF ? (
                canViewFullCPF(effectiveRoles) ? (
                  // Admin: can see and edit the existing value
                  <input
                    type="text"
                    value={formData.cpf}
                    onChange={(e) => {
                      const formatted = formatCPF(e.target.value);
                      setFormData({ ...formData, cpf: formatted });
                    }}
                    className={cn(
                      "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                      darkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"
                    )}
                    placeholder="000.000.000-00"
                    maxLength={14}
                  />
                ) : cpfEditActive ? (
                  // Porteiro — typing new CPF
                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      autoFocus
                      value={cpfDraft}
                      onChange={(e) => setCpfDraft(formatCPF(e.target.value))}
                      className={cn(
                        "flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono",
                        darkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"
                      )}
                      placeholder="000.000.000-00"
                      maxLength={14}
                    />
                    <button
                      type="button"
                      onClick={() => { setCpfEditActive(false); setCpfDraft(''); }}
                      className={cn(
                        "text-xs px-2 py-1 rounded border",
                        darkMode ? "border-gray-600 text-gray-400 hover:text-gray-200" : "border-gray-300 text-gray-500 hover:text-gray-700"
                      )}
                    >
                      Cancelar
                    </button>
                  </div>
                ) : (
                  // Porteiro — masked value + button to change
                  <div className="flex gap-2 items-center">
                    <span className={cn("font-mono flex-1", darkMode ? "text-gray-400" : "text-gray-500")}>
                      {maskCPF(person.cpf || person.document_no, ['porteiro'])}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCpfEditActive(true)}
                      className="text-xs px-2 py-1 rounded border border-blue-400 text-blue-500 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    >
                      Alterar CPF
                    </button>
                  </div>
                )
              ) : (
                <input
                  type="text"
                  value={maskCPF(person.cpf || person.document_no, roles)}
                  readOnly
                  disabled
                  className={cn(
                    "w-full px-3 py-2 border rounded-lg font-mono cursor-not-allowed opacity-60",
                    darkMode ? "bg-gray-800 border-gray-700 text-gray-400" : "bg-gray-100 border-gray-300 text-gray-500"
                  )}
                />
              )
            ) : (
              <CPFDisplay
                cpf={person.cpf || person.document_no}
                className={cn(darkMode ? "text-gray-100" : "text-gray-900")}
                // Scoped to THIS person's customer, so view mode and edit mode agree about who
                // may read the value — CPFDisplay's own hook takes no customer and would fall
                // back to the union of the operator's assignments.
                hookValues={cpfDisplay}
              />
            )}
            {/* The red `*` above promises an enforcement that does not exist for this operator:
                nothing here or on the server refuses a blank CPF from someone who can edit the
                field in place. Say so rather than let them find out. See showsCpfBypassHint. */}
            {isEditing && showsCpfBypassHint({
              required: fieldConfig?.standard_fields?.cpf?.required !== false,
              canEdit: canEditCPF,
              canViewFull: canViewFullCPF(effectiveRoles),
            }) && (
              <p className={cn("mt-1 text-xs", darkMode ? "text-gray-400" : "text-gray-500")}>
                Obrigatório no cadastro, mas sua função de administrador/síndico pode salvar em branco.
              </p>
            )}
          </div>
          )}

          {/* RG — shown unless explicitly disabled in fieldConfig, or when person already has a value */}
          {(fieldConfig?.standard_fields?.rg?.enabled !== false || person.rg) && (
            <div className={fieldColSpanClass('rg')} style={{ order: fieldOrder('rg') }}>
              <label className={cn(
                "block text-sm font-medium mb-1",
                darkMode ? "text-gray-300" : "text-gray-700"
              )}>
                RG{fieldConfig?.standard_fields?.rg?.required && <span className="text-red-500"> *</span>}
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.rg}
                  onChange={(e) => setFormData({ ...formData, rg: e.target.value })}
                  className={cn(
                    "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                    darkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"
                  )}
                  placeholder="00.000.000-0"
                />
              ) : (
                <p className={cn(darkMode ? "text-gray-100" : "text-gray-900")}>{person.rg || '-'}</p>
              )}
            </div>
          )}

          {/* Estrangeiro — the declaration, and the passport that stands in for the CPF.
              Hidden entirely on a Brazilian record that is not being edited: a
              "Estrangeiro: Não" line on every resident is noise in an already dense tab. */}
          {(isEditing || person.is_foreigner) && (
            <div className={fieldColSpanClass('rg')} style={{ order: fieldOrder('rg') }}>
              <label
                htmlFor="person-is-foreigner"
                className={cn("block text-sm font-medium mb-1", darkMode ? "text-gray-300" : "text-gray-700")}
              >
                Estrangeiro
              </label>
              {isEditing ? (
                <div className="flex items-center gap-2 mt-2">
                  <button
                    id="person-is-foreigner"
                    type="button"
                    role="switch"
                    aria-checked={formData.is_foreigner}
                    aria-label="Estrangeiro"
                    onClick={() => setFormData({
                      ...formData,
                      is_foreigner: !formData.is_foreigner,
                      // The CPF row disappears with the declaration; leaving a value behind
                      // would save a document the operator can no longer see.
                      cpf: formData.is_foreigner ? formData.cpf : '',
                    })}
                    className={cn(
                      "h-5 w-9 flex-shrink-0 rounded-full transition-colors",
                      formData.is_foreigner ? "bg-emerald-500" : darkMode ? "bg-gray-600" : "bg-gray-300"
                    )}
                  >
                    <span className={cn(
                      "block h-4 w-4 rounded-full bg-white transition-transform",
                      formData.is_foreigner ? "translate-x-[1.125rem]" : "translate-x-0.5"
                    )} />
                  </button>
                  <span className={cn(
                    "text-xs font-bold tracking-wide",
                    formData.is_foreigner ? "text-emerald-600" : darkMode ? "text-gray-500" : "text-gray-400"
                  )}>
                    {formData.is_foreigner ? 'SIM' : 'NÃO'}
                  </span>
                </div>
              ) : (
                <p className={cn(darkMode ? "text-gray-100" : "text-gray-900")}>Sim</p>
              )}
            </div>
          )}

          {(isEditing ? formData.is_foreigner : person.is_foreigner) && (
            <>
              <div className={fieldColSpanClass('rg')} style={{ order: fieldOrder('rg') }}>
                <label
                  htmlFor="person-passport"
                  className={cn("block text-sm font-medium mb-1", darkMode ? "text-gray-300" : "text-gray-700")}
                >
                  Passaporte{isEditing && <span className="text-red-500"> *</span>}
                </label>
                {isEditing ? (
                  <input
                    id="person-passport"
                    type="text"
                    value={formData.passport_number}
                    onChange={(e) => setFormData({ ...formData, passport_number: normalizePassport(e.target.value) })}
                    className={cn(
                      "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                      darkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"
                    )}
                    placeholder="AB123456"
                    maxLength={20}
                  />
                ) : (
                  <p className={cn(darkMode ? "text-gray-100" : "text-gray-900")}>{person.passport_number || '-'}</p>
                )}
              </div>

              {/* The country is optional at the gate, so an empty one is simply not shown. */}
              {(isEditing || person.country) && (
                <div className={fieldColSpanClass('rg')} style={{ order: fieldOrder('rg') }}>
                  <label
                    htmlFor="person-country"
                    className={cn("block text-sm font-medium mb-1", darkMode ? "text-gray-300" : "text-gray-700")}
                  >
                    País
                  </label>
                  {isEditing ? (
                    <select
                      id="person-country"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      className={cn(
                        "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                        darkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"
                      )}
                    >
                      <option value="">Selecione…</option>
                      {countryOptions().map(({ code, name }) => (
                        <option key={code} value={code}>{name}</option>
                      ))}
                    </select>
                  ) : (
                    <p className={cn(darkMode ? "text-gray-100" : "text-gray-900")}>{countryName(person.country)}</p>
                  )}
                </div>
              )}
            </>
          )}

          {/* Grade de Horário — only shown when time_zones_enabled, and for now restricted to super admins */}
          {timeZonesEnabled && isSuperAdmin && (
            <div style={{ order: 999 }}>
              <label className={cn(
                "block text-sm font-medium mb-1",
                darkMode ? "text-gray-300" : "text-gray-700"
              )}>
                Grade de Horário
              </label>
              {isEditing ? (
                <select
                  value={formData.time_zone_id}
                  onChange={(e) => setFormData({ ...formData, time_zone_id: e.target.value })}
                  className={cn(
                    "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                    darkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"
                  )}
                >
                  <option value="">
                    {responsiblePersonRole && visitorScheduleInheritEnabled
                      ? 'Herdar do responsável pela visita / departamento'
                      : 'Herdar do departamento / Sem restrição'}
                  </option>
                  {timeZones.map(tz => (
                    <option key={tz.id} value={tz.id}>{tz.name}</option>
                  ))}
                </select>
              ) : (
                <div className="space-y-1.5">
                  <p className={cn(darkMode ? "text-gray-100" : "text-gray-900")}>
                    {inheritsFromResponsiblePerson ? 'Herda do responsável pela visita' : (effectiveTimeZone?.name ?? 'Sem restrição')}
                    {inheritedDepartment && (
                      <span className={cn("ml-1.5 text-xs font-normal", darkMode ? "text-gray-500" : "text-gray-400")}>
                        (herdado de {inheritedDepartment.name})
                      </span>
                    )}
                  </p>
                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium",
                      inheritsFromResponsiblePerson
                        ? (darkMode ? "bg-blue-900/40 text-blue-300" : "bg-blue-50 text-blue-700")
                        : !effectiveTimeZone
                          ? (darkMode ? "bg-green-900/40 text-green-300" : "bg-green-50 text-green-700")
                          : scheduleStatus?.within
                            ? (darkMode ? "bg-green-900/40 text-green-300" : "bg-green-50 text-green-700")
                            : (darkMode ? "bg-red-900/40 text-red-300" : "bg-red-50 text-red-700")
                    )}
                  >
                    <span
                      className={cn(
                        "w-1.5 h-1.5 rounded-full flex-shrink-0",
                        inheritsFromResponsiblePerson
                          ? "bg-blue-500"
                          : !effectiveTimeZone || scheduleStatus?.within ? "bg-green-500 animate-pulse" : "bg-red-500"
                      )}
                    />
                    {inheritsFromResponsiblePerson
                      ? 'Horário definido pelo responsável (ou departamento, se ele não tiver grade própria)'
                      : !effectiveTimeZone ? 'Sem restrição — acesso liberado 24h' : scheduleStatus?.label}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Custom fields from admin field config — interleaved with standard fields via the
              same shared order/col-span used in Campos do Cadastro (CSS `order`, see above) */}
          {fieldConfig?.custom_fields && fieldConfig.custom_fields.length > 0 && fieldConfig.custom_fields.map((field: CustomPersonField) => (
            <div
              key={field.id}
              className={field.col_span === 1 ? undefined : 'col-span-2'}
              style={{ order: orderedFields.findIndex(i => i.isCustom && i.key === field.id) }}
            >
              <label className={cn(
                "block text-sm font-medium mb-1",
                darkMode ? "text-gray-300" : "text-gray-700"
              )}>
                {field.label}{field.required && <span className="text-red-500"> *</span>}
              </label>
              {isEditing ? (
                field.type === 'select' ? (
                  <select
                    value={formData.extra_data[field.id] ?? ''}
                    onChange={(e) => setFormData({ ...formData, extra_data: { ...formData.extra_data, [field.id]: e.target.value } })}
                    className={cn(
                      "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                      darkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"
                    )}
                  >
                    <option value="">Selecione...</option>
                    {field.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                ) : field.type === 'textarea' ? (
                  <textarea
                    value={formData.extra_data[field.id] ?? ''}
                    onChange={(e) => setFormData({ ...formData, extra_data: { ...formData.extra_data, [field.id]: e.target.value } })}
                    rows={3}
                    className={cn(
                      "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none",
                      darkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"
                    )}
                  />
                ) : field.type === 'date' ? (
                  <input
                    type="text"
                    placeholder="DD/MM/AAAA"
                    value={formData.extra_data[field.id] ?? ''}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
                      let masked = digits;
                      if (digits.length > 2) masked = digits.slice(0, 2) + '/' + digits.slice(2);
                      if (digits.length > 4) masked = digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4);
                      setFormData({ ...formData, extra_data: { ...formData.extra_data, [field.id]: masked } });
                    }}
                    maxLength={10}
                    className={cn(
                      "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                      darkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"
                    )}
                  />
                ) : field.type === 'phone' ? (
                  <input
                    type="tel"
                    placeholder="(00) 00000-0000"
                    value={formData.extra_data[field.id] ?? ''}
                    onChange={(e) => {
                      const d = e.target.value.replace(/\D/g, '').slice(0, 11);
                      let m = d;
                      if (d.length > 2) m = `(${d.slice(0, 2)}) ${d.slice(2)}`;
                      if (d.length > 7) m = `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
                      setFormData({ ...formData, extra_data: { ...formData.extra_data, [field.id]: m } });
                    }}
                    maxLength={16}
                    className={cn(
                      "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                      darkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"
                    )}
                  />
                ) : (
                  <input
                    type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : 'text'}
                    value={formData.extra_data[field.id] ?? ''}
                    onChange={(e) => setFormData({ ...formData, extra_data: { ...formData.extra_data, [field.id]: e.target.value } })}
                    className={cn(
                      "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                      darkMode ? "bg-gray-800 border-gray-600 text-gray-100" : "bg-white border-gray-300 text-gray-900"
                    )}
                  />
                )
              ) : (
                <p className={cn(darkMode ? "text-gray-100" : "text-gray-900")}>
                  {person.extra_data?.[field.id] || '-'}
                </p>
              )}
              {/* A CNH expiry means something right now, and this is where a porteiro looks
                  someone up — the same read the registration form gives, on both the saved
                  line and the draft being typed. Advisory: it blocks nothing. */}
              {isCnhValidadeField(field.label) && (
                <CnhValidadeHint
                  value={pickCnhHintValue({
                    isEditing,
                    draft: formData.extra_data?.[field.id],
                    saved: person.extra_data?.[field.id],
                  })}
                  darkMode={darkMode}
                />
              )}
            </div>
          ))}

          {/* Department selector - Only show for empresa customers with staff role (edit mode only; view mode shown below) */}
          {person.customer?.type === 'empresa' && activeStaffRole && isEditing && (
            <div className="col-span-2">
              <DepartmentManager
                value={formData.department_ids}
                customerId={person.customer.id}
                onChange={(v) => setFormData({ ...formData, department_ids: v })}
                relations={formData.department_relations}
                onRelationChange={(deptId, relation) => setFormData({
                  ...formData,
                  department_relations: { ...formData.department_relations, [deptId]: relation },
                })}
                darkMode={darkMode}
              />
            </div>
          )}

          {/* Cargo (position) field - Show for all staff roles in edit mode */}
          {activeStaffRole && isEditing && (
            <div className="col-span-2">
              <label className={cn("block text-xs font-medium mb-1", darkMode ? "text-gray-400" : "text-gray-500")}>
                Cargo
              </label>
              <input
                type="text"
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                placeholder="Ex: Gerente, Analista, Recepcionista..."
                className={cn(
                  "w-full px-3 py-2 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/30",
                  darkMode
                    ? "bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500 focus:border-blue-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-400"
                )}
              />
            </div>
          )}

          {/* Units selector - Only show for condominio customers with resident role (edit mode only; view mode shown below) */}
          {person.customer?.type === 'condominio' && activeResidentRole && isEditing && (
            <div className="col-span-2">
              <UnitAssignmentManager
                value={formData.unit_assignments}
                customerId={person.customer.id}
                onChange={(v) => {
                  console.log('[OverviewTab] UnitAssignmentManager onChange:', v);
                  setFormData(prev => ({ ...prev, unit_assignments: v }));
                }}
                onPendingChange={setUnitManagerHasPending}
                darkMode={darkMode}
                condoType={(person.customer as any)?.condo_data?.condo_type}
              />
            </div>
          )}

          {/* Unit selector - Only show for condominio customers with condo employee role (edit mode only; view mode shown below) */}
          {person.customer?.type === 'condominio' && activeCondoEmployeeRole && isEditing && (
            <div className="col-span-2">
              <label className={cn("block text-xs font-medium mb-1", darkMode ? "text-gray-400" : "text-gray-500")}>
                Unidade
              </label>
              <UnitSelector
                customerId={person.customer.id}
                value={employeeUnit}
                onChange={setEmployeeUnit}
                condoType={(person.customer as any)?.condo_data?.condo_type}
                darkMode={darkMode}
              />
            </div>
          )}

          {/* Intercom notification checkbox - Only show for visitor roles in edit mode */}
          {activeVisitorRole && isEditing && (
            <div className="col-span-2">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="requires_intercom"
                  checked={formData.requires_intercom}
                  onChange={(e) => setFormData({ ...formData, requires_intercom: e.target.checked })}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label
                  htmlFor="requires_intercom"
                  className={cn(
                    "text-sm cursor-pointer",
                    darkMode ? "text-gray-300" : "text-gray-700"
                  )}
                >
                  <div className="font-medium flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Interfonar ao Chegar
                  </div>
                  <div className={cn(
                    "mt-1 text-xs",
                    darkMode ? "text-gray-400" : "text-gray-500"
                  )}>
                    Avisar o morador por interfone quando o visitante chegar na portaria
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* History privacy toggle — resident's individual access events hidden from unit-mates.
              Admin/super_admin only (level 90+): a privacy/safety-sensitive setting, not a
              routine profile edit, so it's gated tighter than the rest of this tab and saves
              immediately via its own dedicated endpoint rather than riding along with the
              general "Salvar" button. */}
          {activeResidentRole && isEditing && hasMinimumRoleLevel(roles, 90) && (
            <div className="col-span-2">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="hide_access_history_from_unit"
                  checked={!!person.hide_access_history_from_unit}
                  disabled={isSavingHistoryPrivacy}
                  onChange={async (e) => {
                    const next = e.target.checked;
                    setIsSavingHistoryPrivacy(true);
                    try {
                      await api.patch(`/customers/${person.customer?.id}/people/${person.id}/history-privacy`, {
                        hide_access_history_from_unit: next,
                      });
                      toast.success(next
                        ? 'Histórico de acessos ocultado dos demais moradores da unidade'
                        : 'Histórico de acessos voltou a ser visível para a unidade');
                      onRefresh?.();
                    } catch {
                      toast.error('Falha ao atualizar preferência de privacidade');
                    } finally {
                      setIsSavingHistoryPrivacy(false);
                    }
                  }}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label
                  htmlFor="hide_access_history_from_unit"
                  className={cn(
                    "text-sm cursor-pointer",
                    darkMode ? "text-gray-300" : "text-gray-700"
                  )}
                >
                  <div className="font-medium">Ocultar histórico de acessos da unidade</div>
                  <div className={cn(
                    "mt-1 text-xs",
                    darkMode ? "text-gray-400" : "text-gray-500"
                  )}>
                    Outros moradores da mesma unidade não verão os acessos individuais desta pessoa. Não afeta a própria pessoa nem a visão de administradores.
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* ── Vinculações: Units (residents) or Departments (staff) — view mode only ── */}
      {!isEditing && (activeResidentRole || activeStaffRole) && (
        <div className={cn(
          "rounded-xl border overflow-hidden",
          darkMode ? "border-gray-700" : "border-gray-200"
        )}>
          {/* Header */}
          <div className={cn(
            "px-4 py-2.5 flex items-center gap-2 border-b",
            darkMode ? "bg-gray-800/60 border-gray-700" : "bg-gray-50 border-gray-200"
          )}>
            {activeResidentRole && <Building2 className="w-3.5 h-3.5 text-gray-400" />}
            <span className={cn(
              "text-[10px] font-semibold uppercase tracking-widest",
              darkMode ? "text-gray-400" : "text-gray-500"
            )}>
              {activeResidentRole ? 'Unidades' : 'Departamentos'}
            </span>
          </div>

          {/* ── UNITS ── */}
          {activeResidentRole && (() => {
            const unitAssignments: UnitAssignment[] = activeResidentRole.role_metadata?.unit_assignments ||
              (activeResidentRole.role_metadata?.unit_ids ||
                (activeResidentRole.role_metadata?.unit_id ? [activeResidentRole.role_metadata.unit_id] : [])
              ).map((id: string) => ({
                unit_id: id,
                unit_number: activeResidentRole.role_metadata?.unit_number,
                block_name: activeResidentRole.role_metadata?.block_name,
                street_name: activeResidentRole.role_metadata?.street_name,
                street_number: activeResidentRole.role_metadata?.street_number,
                relation_type: activeResidentRole.role_metadata?.relation_type || 'proprietario',
                parentesco: activeResidentRole.role_metadata?.parentesco,
              }));

            const formatRelationType = (type: string): string => ({
              proprietario: 'Proprietário', inquilino: 'Inquilino',
              dependente: 'Dependente', familiar: 'Familiar'
            } as Record<string, string>)[type] || type;

            const relationStyle = (type: string): string => (({
              proprietario: darkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-50 text-indigo-700',
              inquilino:    darkMode ? 'bg-amber-900/40 text-amber-300'   : 'bg-amber-50 text-amber-700',
              dependente:   darkMode ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-50 text-emerald-700',
              familiar:     darkMode ? 'bg-blue-900/40 text-blue-300'  : 'bg-blue-50 text-blue-700',
            } as Record<string, string>)[type] || (darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'));

            if (unitAssignments.length === 0) {
              return (
                <p className={cn("px-4 py-6 text-sm text-center", darkMode ? "text-gray-500" : "text-gray-400")}>
                  Sem unidade atribuída
                </p>
              );
            }

            return (
              <div>
                {unitAssignments.map((assignment, idx) => {
                  const isExpanded = expandedUnitId === assignment.unit_id;
                  const pagedResidents = unitResidents.slice(unitResidentsPage * UNIT_PAGE_SIZE, (unitResidentsPage + 1) * UNIT_PAGE_SIZE);
                  const totalPages = Math.ceil(unitResidents.length / UNIT_PAGE_SIZE);
                  return (
                    <div key={assignment.unit_id}>
                      <div
                        className={cn(
                          "flex items-center gap-3 px-4 py-4 transition-colors group",
                          idx > 0 && (darkMode ? "border-t border-gray-700" : "border-t border-gray-100"),
                          isExpanded
                            ? (darkMode ? "bg-gray-700/40" : "bg-blue-50/70")
                            : (darkMode ? "hover:bg-gray-700/40" : "hover:bg-blue-50/50")
                        )}
                      >
                        {/* Clickable unit area → navigate to unit page */}
                        <button
                          onClick={() => {
                            const unitNameParts: string[] = [];
                            if (assignment.block_name) unitNameParts.push(assignment.block_name);
                            if (assignment.unit_number) unitNameParts.push(assignment.unit_number);
                            if (assignment.street_name) unitNameParts.push(assignment.street_number ? `${assignment.street_name}, ${assignment.street_number}` : assignment.street_name);
                            const unitName = unitNameParts.join(' – ');
                            if (onUnitClick) {
                              closePersonDetail();
                              onUnitClick(assignment.unit_id, unitName);
                            } else {
                              closePersonDetail();
                              navigate(`/units?unit=${assignment.unit_id}`);
                            }
                          }}
                          className="flex items-center gap-3 flex-1 text-left min-w-0"
                        >
                          <div className={cn(
                            "w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors",
                            darkMode ? "bg-blue-900/30 group-hover:bg-blue-900/50" : "bg-blue-50 group-hover:bg-blue-100"
                          )}>
                            <Building2 className={cn("w-4 h-4", darkMode ? "text-blue-400" : "text-blue-600")} />
                          </div>
                          <div className="min-w-0 flex-1">
                            {(() => {
                              const condoType = (person.customer as any)?.condo_data?.condo_type;
                              const parts: string[] = [];
                              if (assignment.block_name) parts.push(`${getBlockLabel(condoType)} ${assignment.block_name}`);
                              parts.push(assignment.unit_number ? `${getUnitLabel(condoType)} ${assignment.unit_number}` : '—');
                              const blockUnitLabel = parts.join(' · ');
                              // Street addressing, when present, is the more specific/actionable info —
                              // shown as the prominent line, with Quadra/Lote or Bloco/Unidade as the
                              // secondary reference underneath (reversed from the no-street case).
                              if (assignment.street_name) {
                                const streetLabel = assignment.street_number ? `${assignment.street_name}, ${assignment.street_number}` : assignment.street_name;
                                return (
                                  <>
                                    <p className={cn("font-semibold text-sm leading-tight truncate", darkMode ? "text-gray-100" : "text-gray-900")}>
                                      {streetLabel}
                                    </p>
                                    <p className={cn("text-xs leading-tight mt-0.5 truncate", darkMode ? "text-gray-400" : "text-gray-500")}>
                                      {blockUnitLabel}
                                    </p>
                                  </>
                                );
                              }
                              return (
                                <p className={cn("font-semibold text-sm leading-tight truncate", darkMode ? "text-gray-100" : "text-gray-900")}>
                                  {blockUnitLabel}
                                </p>
                              );
                            })()}
                            <span className={cn(
                              "inline-block mt-1.5 text-[11px] px-2 py-0.5 rounded-full font-medium",
                              relationStyle(assignment.relation_type)
                            )}>
                              {formatRelationType(assignment.relation_type)}
                              {assignment.parentesco && (
                                <> · {getParentescoLabel(assignment.parentesco)}</>
                              )}
                            </span>
                          </div>
                        </button>

                        {/* Expand/collapse chevron button */}
                        <button
                          onClick={() => {
                            if (isExpanded) {
                              setExpandedUnitId(null);
                            } else {
                              setExpandedUnitId(assignment.unit_id);
                              fetchUnitResidents(assignment.unit_id);
                            }
                          }}
                          className={cn(
                            "p-1.5 rounded-lg transition-colors flex-shrink-0",
                            darkMode ? "text-gray-500 hover:text-gray-300 hover:bg-gray-700" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                          )}
                          title="Ver moradores"
                        >
                          {isExpanded
                            ? <ChevronUp className="w-4 h-4" />
                            : <ChevronDown className="w-4 h-4" />
                          }
                        </button>
                      </div>

                      {/* Expanded unit residents */}
                      {isExpanded && (
                        <div className={cn("border-t", darkMode ? "border-gray-700 bg-gray-800/30" : "border-gray-100 bg-gray-50/70")}>
                          <div className={cn("flex items-center gap-2 px-4 py-2.5 border-b", darkMode ? "border-gray-700" : "border-gray-100")}>
                            <Users className={cn("w-3.5 h-3.5", darkMode ? "text-gray-400" : "text-gray-500")} />
                            <span className={cn("text-xs font-medium", darkMode ? "text-gray-400" : "text-gray-500")}>
                              Outros moradores
                            </span>
                            {!unitResidentsLoading && (
                              <span className={cn("text-xs ml-auto", darkMode ? "text-gray-500" : "text-gray-400")}>
                                {unitResidents.length === 0 ? 'Nenhum' : `${unitResidents.length} pessoa${unitResidents.length !== 1 ? 's' : ''}`}
                              </span>
                            )}
                          </div>

                          {unitResidentsLoading ? (
                            <div className="flex items-center justify-center py-6">
                              <RefreshCw className={cn("w-4 h-4 animate-spin", darkMode ? "text-gray-500" : "text-gray-400")} />
                            </div>
                          ) : unitResidents.length === 0 ? (
                            <p className={cn("px-4 py-4 text-xs text-center", darkMode ? "text-gray-500" : "text-gray-400")}>
                              Nenhum outro morador nesta unidade
                            </p>
                          ) : (
                            <>
                              {pagedResidents.map((resident: any, rIdx: number) => {
                                const name = [resident.first_name, resident.last_name].filter(Boolean).join(' ') || resident.name || '—';
                                const photoUrl = getAbsoluteImageUrl(resident.profile_picture_url || resident.identity_photo_url);
                                // relation_type is returned directly by the residents endpoint
                                const relationType: string = resident.relation_type || '';
                                return (
                                  <button
                                    key={resident.id}
                                    onClick={() => openPersonDetail(resident.id, true, darkMode)}
                                    className={cn(
                                      "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors",
                                      rIdx > 0 && (darkMode ? "border-t border-gray-700/50" : "border-t border-gray-100"),
                                      darkMode ? "hover:bg-gray-700/40" : "hover:bg-blue-50/60"
                                    )}
                                  >
                                    <div className={cn("w-7 h-9 rounded-md overflow-hidden flex-shrink-0 flex items-center justify-center text-xs font-semibold",
                                      darkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"
                                    )}>
                                      {photoUrl
                                        ? <img src={photoUrl} alt={name} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                        : name.charAt(0).toUpperCase()
                                      }
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className={cn("text-sm font-medium truncate leading-tight", darkMode ? "text-gray-200" : "text-gray-800")}>{name}</p>
                                      {relationType ? (
                                        <span className={cn("inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium", relationStyle(relationType))}>
                                          {formatRelationType(relationType)}
                                        </span>
                                      ) : resident.phone ? (
                                        <p className={cn("text-xs truncate mt-0.5", darkMode ? "text-gray-500" : "text-gray-400")}>{resident.phone}</p>
                                      ) : null}
                                    </div>
                                    <ChevronRightIcon className={cn("w-3.5 h-3.5 flex-shrink-0", darkMode ? "text-gray-600" : "text-gray-300")} />
                                  </button>
                                );
                              })}

                              {totalPages > 1 && (
                                <div className={cn("flex items-center justify-between px-4 py-2 border-t", darkMode ? "border-gray-700" : "border-gray-100")}>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setUnitResidentsPage(p => Math.max(0, p - 1)); }}
                                    disabled={unitResidentsPage === 0}
                                    className={cn("p-1 rounded disabled:opacity-30", darkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-200 text-gray-500")}
                                  >
                                    <ChevronLeft className="w-4 h-4" />
                                  </button>
                                  <span className={cn("text-xs", darkMode ? "text-gray-500" : "text-gray-400")}>
                                    {unitResidentsPage + 1} / {totalPages}
                                  </span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setUnitResidentsPage(p => Math.min(totalPages - 1, p + 1)); }}
                                    disabled={unitResidentsPage === totalPages - 1}
                                    className={cn("p-1 rounded disabled:opacity-30", darkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-200 text-gray-500")}
                                  >
                                    <ChevronRightIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* ── CARGO row (inside departments card) ── */}
          {activeStaffRole && (
            <div className={cn(
              "px-4 py-2.5 flex items-center gap-2 border-b",
              darkMode ? "border-gray-700/60" : "border-gray-100"
            )}>
              <span className={cn("text-[10px] font-semibold uppercase tracking-widest w-12 flex-shrink-0", darkMode ? "text-gray-500" : "text-gray-400")}>
                Cargo
              </span>
              <span className={cn("w-px h-3 flex-shrink-0", darkMode ? "bg-gray-700" : "bg-gray-200")} />
              {currentPosition ? (
                <span className={cn("text-sm font-medium", darkMode ? "text-gray-200" : "text-gray-800")}>
                  {currentPosition}
                </span>
              ) : (
                <span className={cn("text-xs italic", darkMode ? "text-gray-600" : "text-gray-400")}>
                  Não informado
                </span>
              )}
            </div>
          )}

          {/* ── DEPARTMENTS ── */}
          {activeStaffRole && (() => {
            const depts = activeStaffRole.role_metadata?.departments || [];
            const deptIds = activeStaffRole.role_metadata?.department_ids || [];
            const singleDeptId = activeStaffRole.role_metadata?.department_id;
            const legacyName = activeStaffRole.role_metadata?.department_name;
            // Single-department roles carry a real, resolvable department_id alongside the
            // resolved department_name — use it so the chip is clickable, same as the
            // multi-department case. Only fall back to the id-less 'legacy' placeholder
            // (disables the click below) when no real id was ever resolved.
            const items: { id: string; name: string }[] =
              depts.length > 0 ? depts :
              deptIds.length > 0 ? deptIds.map((id: string) => ({ id, name: id })) :
              (singleDeptId && legacyName) ? [{ id: singleDeptId, name: legacyName }] :
              legacyName ? [{ id: 'legacy', name: legacyName }] : [];

            if (items.length === 0) {
              return (
                <p className={cn("px-4 py-6 text-sm text-center", darkMode ? "text-gray-500" : "text-gray-400")}>
                  Sem departamento atribuído
                </p>
              );
            }

            return (
              <div className="px-4 py-3 flex flex-wrap gap-2">
                {items.map((dept) => (
                  <button
                    key={dept.id}
                    type="button"
                    onClick={onDepartmentClick && dept.id !== 'legacy'
                      ? () => { closePersonDetail(); onDepartmentClick!(dept.id, dept.name); }
                      : undefined}
                    className={cn(
                      // Column, not row: the department is the heading and the relation is a
                      // caption under it. Side by side, the pill competed with the name for the
                      // eye and the chip grew wide enough to wrap after two departments.
                      "flex flex-col items-start gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors",
                      onDepartmentClick && dept.id !== 'legacy' ? "cursor-pointer" : "cursor-default",
                      darkMode
                        ? "bg-gray-800/80 border-gray-700 text-gray-200 hover:bg-gray-700 hover:border-gray-600"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm"
                    )}
                  >
                    <span className="leading-tight">{dept.name}</span>
                    {/* The relation this person holds HERE — each department membership is its
                        own staff role, so two departments can hold two different relations. */}
                    <span className={cn(
                      "text-[11px] px-2 py-0.5 rounded-full font-medium leading-tight",
                      departmentRelationTone(currentDepartmentRelations[dept.id], darkMode)
                    )}>
                      {departmentRelationLabel(currentDepartmentRelations[dept.id])}
                    </span>
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Unidade (condo employees) — view mode only, single unit, no relation_type ── */}
      {!isEditing && activeCondoEmployeeRole && (
        <div className={cn(
          "rounded-xl border overflow-hidden",
          darkMode ? "border-gray-700" : "border-gray-200"
        )}>
          <div className={cn(
            "px-4 py-2.5 flex items-center gap-2 border-b",
            darkMode ? "bg-gray-800/60 border-gray-700" : "bg-gray-50 border-gray-200"
          )}>
            <Building2 className="w-3.5 h-3.5 text-gray-400" />
            <span className={cn(
              "text-[10px] font-semibold uppercase tracking-widest",
              darkMode ? "text-gray-400" : "text-gray-500"
            )}>
              Unidade
            </span>
          </div>
          {condoEmployeeMetadata?.unit_id ? (
            <button
              type="button"
              onClick={() => {
                const unitNameParts: string[] = [];
                if (condoEmployeeMetadata.block_name) unitNameParts.push(condoEmployeeMetadata.block_name);
                if (condoEmployeeMetadata.unit_number) unitNameParts.push(condoEmployeeMetadata.unit_number);
                const unitName = unitNameParts.join(' – ');
                if (onUnitClick) {
                  closePersonDetail();
                  onUnitClick(condoEmployeeMetadata.unit_id, unitName);
                } else {
                  closePersonDetail();
                  navigate(`/units?unit=${condoEmployeeMetadata.unit_id}`);
                }
              }}
              className={cn(
                "w-full flex items-center gap-3 px-4 py-4 text-left transition-colors",
                darkMode ? "hover:bg-gray-700/40" : "hover:bg-blue-50/50"
              )}
            >
              <div className={cn(
                "w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0",
                darkMode ? "bg-blue-900/30" : "bg-blue-50"
              )}>
                <Building2 className={cn("w-4 h-4", darkMode ? "text-blue-400" : "text-blue-600")} />
              </div>
              <div className="min-w-0 flex-1">
                {(() => {
                  const condoType = (person.customer as any)?.condo_data?.condo_type;
                  const parts: string[] = [];
                  if (condoEmployeeMetadata.block_name) parts.push(`${getBlockLabel(condoType)} ${condoEmployeeMetadata.block_name}`);
                  parts.push(condoEmployeeMetadata.unit_number ? `${getUnitLabel(condoType)} ${condoEmployeeMetadata.unit_number}` : '—');
                  const blockUnitLabel = parts.join(' · ');
                  if (condoEmployeeMetadata.street_name) {
                    const streetLabel = condoEmployeeMetadata.street_number ? `${condoEmployeeMetadata.street_name}, ${condoEmployeeMetadata.street_number}` : condoEmployeeMetadata.street_name;
                    return (
                      <>
                        <p className={cn("font-semibold text-sm leading-tight truncate", darkMode ? "text-gray-100" : "text-gray-900")}>
                          {streetLabel}
                        </p>
                        <p className={cn("text-xs leading-tight mt-0.5 truncate", darkMode ? "text-gray-400" : "text-gray-500")}>
                          {blockUnitLabel}
                        </p>
                      </>
                    );
                  }
                  return (
                    <p className={cn("font-semibold text-sm leading-tight truncate", darkMode ? "text-gray-100" : "text-gray-900")}>
                      {blockUnitLabel}
                    </p>
                  );
                })()}
              </div>
            </button>
          ) : (
            <p className={cn("px-4 py-6 text-sm text-center", darkMode ? "text-gray-500" : "text-gray-400")}>
              Sem unidade atribuída
            </p>
          )}
        </div>
      )}

      {/* Role Details - Show additional role information if available */}
      {person.roles && person.roles.length > 0 && person.roles.some(role =>
        role.role_type === 'visitor' ||
        role.role_type === 'service_provider' ||
        (role.role_metadata && (
          role.role_metadata.company_name ||
          role.role_metadata.service_type ||
          role.role_metadata.visiting_resident_id ||
          role.role_metadata.purpose
        ))
      ) && (
        <div>
          {/* The COUNT is part of the heading: three stacked blocks separated by a hairline read
              as one long block, and the whole point of this section is that a person can hold
              several clearances at once. */}
          <p className={cn("text-[10px] font-semibold uppercase tracking-widest mb-3", darkMode ? "text-gray-500" : "text-gray-400")}>
            {rolesSectionTitle((person.roles || []).filter((r: any) =>
              r.role_type === 'visitor' ||
              r.role_type === 'service_provider' ||
              (r.role_metadata && (
                r.role_metadata.company_name ||
                r.role_metadata.service_type ||
                r.role_metadata.visiting_resident_id ||
                r.role_metadata.purpose
              ))
            ))}
            {(() => {
              const shown = (person.roles || []).filter((r: any) =>
                r.role_type === 'visitor' ||
                r.role_type === 'service_provider' ||
                (r.role_metadata && (
                  r.role_metadata.company_name ||
                  r.role_metadata.service_type ||
                  r.role_metadata.visiting_resident_id ||
                  r.role_metadata.purpose
                ))
              ).length;
              return shown > 1 ? (
                <span className={cn("ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-bold", darkMode ? "bg-gray-800 text-gray-400" : "bg-gray-100 text-gray-500")}>
                  {shown}
                </span>
              ) : null;
            })()}
          </p>
          {/* Each one a CARD, not a hairline-separated block: the three Fernando Diniz holds were
              indistinguishable until you read three fields down. */}
          <div className="space-y-2">
            {person.roles.map((role) => {
              // Only render role card if it has metadata to display
              const hasMetadata = role.role_type === 'visitor' ||
                role.role_type === 'service_provider' ||
                (role.role_metadata && (
                  role.role_metadata.company_name ||
                  role.role_metadata.service_type ||
                  role.role_metadata.visiting_resident_id ||
                  role.role_metadata.purpose
                ));

              if (!hasMetadata) return null;

              return (
                <div key={role.id} className={cn(
                  "rounded-lg border p-3",
                  darkMode ? "border-gray-700 bg-gray-900/40" : "border-gray-200 bg-gray-50/60"
                )}>
                  {/* Role type + status row.
                      The unit is NOT repeated here: it used to ride in the heading AND in the
                      "Unidade destino" field below, saying the same address twice in two
                      different compositions — which is why the de-duplication never caught it.
                      The field keeps it, because it is labelled and reads as an address. */}
                  <div className="flex items-start justify-between mb-1.5 gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={cn("text-xs font-semibold uppercase tracking-widest", darkMode ? "text-gray-400" : "text-gray-500")}>
                          {translatePersonTypeToPortuguese(role.role_type)}
                        </span>
                      </div>
                    </div>
                    {/* flex-shrink-0: this group carries the status and the action, and when the
                        left label grew (role type + a street address + the kind chip) it was
                        squeezed until "ATIVO" was clipped by the card's own edge. */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {role.is_authorized && (
                        <span className={cn("text-xs uppercase tracking-wide", darkMode ? "text-gray-400" : "text-gray-500")}>
                          Autorizado
                        </span>
                      )}
                      {/* What the CLEARANCE is, not whether the row is active: a visita única
                          pre-registered on the 3rd and never used is still is_active on the 16th,
                          and "● ATIVO" about access nobody has used is read by a porteiro before
                          opening a gate. See roleStatusLabel. */}
                      {(() => {
                        const tone = roleStatusTone(role);
                        return (
                          <span className={cn(
                            "text-xs uppercase tracking-wide font-medium whitespace-nowrap",
                            tone === 'live'
                              ? darkMode ? "text-emerald-400" : "text-emerald-600"
                              : tone === 'pending'
                                ? darkMode ? "text-amber-400" : "text-amber-600"
                                : darkMode ? "text-gray-500" : "text-gray-400"
                          )}>
                            ● {roleStatusLabel(role)}
                          </span>
                        );
                      })()}

                      {/* Only a LIVE clearance, and only when the host can revoke — see
                          canRevokeRole. A word rather than a bin: beside a person's name a trash
                          icon reads as "delete this person", which is not what this does. */}
                      {!readOnly && onRevokeRole && canRevokeRole(role) && (
                        <button
                          onClick={() => onRevokeRole(role)}
                          title="Remover liberação"
                          aria-label="Remover liberação"
                          className={cn(
                            "px-2 py-0.5 text-xs font-medium rounded-lg border transition-colors",
                            darkMode
                              ? "text-gray-400 border-gray-700 hover:text-red-400 hover:border-red-900/60 hover:bg-red-950/30"
                              : "text-gray-500 border-gray-200 hover:text-red-600 hover:border-red-200 hover:bg-red-50"
                          )}
                        >
                          Remover
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Fields */}
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                    {/* WHAT this card is, first and in words. Without it a clearance reads as a
                        registration detail, and which kind it is changes what the porteiro does
                        when the person turns up. One field, one vocabulary — it replaced a chip
                        in the heading AND an "Entradas: Entrada única" field that said the same
                        thing differently. */}
                    {liberacaoKindLabel(role) && (
                      <div className="col-span-2">
                        <p className={cn("text-[10px] uppercase tracking-widest mb-0.5", darkMode ? "text-gray-500" : "text-gray-400")}>Tipo de entrada</p>
                        <p className={cn("text-sm font-medium", darkMode ? "text-gray-100" : "text-gray-900")}>{liberacaoKindLabel(role)}</p>
                      </div>
                    )}
                    {/* One range instead of two stacked fields — "válido desde" and "válido até"
                        are a single fact and were costing two rows each. */}
                    <div>
                      <p className={cn("text-[10px] uppercase tracking-widest mb-0.5", darkMode ? "text-gray-500" : "text-gray-400")}>Válido</p>
                      <p className={cn("text-sm", darkMode ? "text-gray-200" : "text-gray-700")}>
                        {/* The sentence utils/liberacoes composes for the same clearance on the
                            Liberados cards — "Dia 10/10/2026 às 14:30" for a one-off (an
                            APPOINTMENT, not a window), a range for a período, "A partir de …" when
                            there is no end. See roleValidityLabel. */}
                        {roleValidityLabel(role) ?? '—'}
                      </p>
                    </div>
                    {role.role_metadata?.purpose && (
                      <div className="col-span-2">
                        <p className={cn("text-[10px] uppercase tracking-widest mb-0.5", darkMode ? "text-gray-500" : "text-gray-400")}>Propósito</p>
                        <p className={cn("text-sm", darkMode ? "text-gray-200" : "text-gray-700")}>{role.role_metadata.purpose}</p>
                      </div>
                    )}
                    {role.role_metadata?.company_name && (
                      <div>
                        <p className={cn("text-[10px] uppercase tracking-widest mb-0.5", darkMode ? "text-gray-500" : "text-gray-400")}>Empresa</p>
                        <p className={cn("text-sm", darkMode ? "text-gray-200" : "text-gray-700")}>{role.role_metadata.company_name}</p>
                      </div>
                    )}
                    {role.role_metadata?.service_type && (
                      <div>
                        <p className={cn("text-[10px] uppercase tracking-widest mb-0.5", darkMode ? "text-gray-500" : "text-gray-400")}>Tipo de Serviço</p>
                        <p className={cn("text-sm", darkMode ? "text-gray-200" : "text-gray-700")}>{role.role_metadata.service_type}</p>
                      </div>
                    )}

                    {/* Service provider fields */}
                    {role.role_type === 'service_provider' && (() => {
                      const meta = role.role_metadata || {};
                      const windowStart: string = meta.entry_window_start || '';
                      const windowEnd: string = meta.entry_window_end || '';
                      const dayLabels = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
                      // Null when the role records none — see roleAllowedDays. The old default of
                      // Seg–Sex put a restriction nobody agreed to on every prestador card.
                      const allowedDays = roleAllowedDays(role);
                      const singleEntry = isSingleEntryRole(role);
                      const visitorState: string = meta.visitor_state || '';
                      const stateText: Record<string, string> = {
                        inside:         'Dentro',
                        awaiting_entry: 'Aguardando entrada',
                        expired:        'Não entrou hoje',
                        exited:         'Saiu',
                      };

                      return (
                        <>
                          {/* No "Unidade" here: the shared "Unidade destino" field below prints
                              the same thing for visitantes and prestadores alike, and a prestador
                              card was showing the unit THREE times — heading, here, and there. */}
                          {stateText[visitorState] && (
                            <div>
                              <p className={cn("text-[10px] uppercase tracking-widest mb-0.5", darkMode ? "text-gray-500" : "text-gray-400")}>Status hoje</p>
                              <p className={cn("text-sm", darkMode ? "text-gray-200" : "text-gray-700")}>{stateText[visitorState]}</p>
                            </div>
                          )}
                          {(windowStart || windowEnd) && (
                            <div>
                              <p className={cn("text-[10px] uppercase tracking-widest mb-0.5", darkMode ? "text-gray-500" : "text-gray-400")}>Janela de entrada</p>
                              <p className={cn("text-sm", darkMode ? "text-gray-200" : "text-gray-700")}>{windowStart} — {windowEnd}</p>
                            </div>
                          )}
                          {meta.contractor_name && !isDuplicateValue(meta.contractor_name, [role.role_metadata?.company_name]) && (
                            <div>
                              <p className={cn("text-[10px] uppercase tracking-widest mb-0.5", darkMode ? "text-gray-500" : "text-gray-400")}>Empresa contratada</p>
                              <p className={cn("text-sm", darkMode ? "text-gray-200" : "text-gray-700")}>{meta.contractor_name}</p>
                            </div>
                          )}
                          {/* Only what the clearance actually says. A single entry is already
                              named by "Tipo de entrada" above, so here it means: no weekday strip,
                              which would imply a recurring arrangement. */}
                          {singleEntry ? null : allowedDays ? (
                            <div className="col-span-2">
                              <p className={cn("text-[9px] uppercase tracking-widest mb-1", darkMode ? "text-gray-500" : "text-gray-400")}>Dias permitidos</p>
                              <div className="flex gap-1">
                                {dayLabels.map((label, idx) => (
                                  <span key={idx} className={cn(
                                    "text-xs w-7 text-center py-0.5 rounded",
                                    allowedDays.includes(idx)
                                      ? darkMode ? "text-gray-100 font-semibold" : "text-gray-800 font-semibold"
                                      : darkMode ? "text-gray-600" : "text-gray-300"
                                  )}>
                                    {label}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </>
                      );
                    })()}

                    {/* Visitor / service_provider: WHICH unit — the card's only mention of it.
                        Street address on top, quadra/lote under it: the porteiro reads the
                        address out loud and uses the quadra/lote to place it on the map. */}
                    {(role.role_type === 'visitor' || role.role_type === 'service_provider') && (
                      <RoleUnitField
                        role={role}
                        condoType={(person.customer as any)?.condo_data?.condo_type}
                        darkMode={darkMode}
                        onOpenUnit={(unitId, label) => {
                          closePersonDetail();
                          // The context handler when the host provides one — the route the
                          // modal's top strip takes, which works inside the Operational view
                          // too, where a bare /units navigation goes nowhere.
                          if (onUnitClick) onUnitClick(unitId, label);
                          else navigate(`/units?unit=${unitId}`);
                        }}
                      />
                    )}

                    {/* Visitor / service_provider: authorized by */}
                    {(role.role_type === 'visitor' || role.role_type === 'service_provider') && (role.role_metadata?.authorized_by_name || role.role_metadata?.responsible_person_name) && (
                      <div className="col-span-2">
                        <p className={cn("text-[10px] uppercase tracking-widest mb-0.5", darkMode ? "text-gray-500" : "text-gray-400")}>Autorizado por</p>
                        {(() => {
                          const name = role.role_metadata.authorized_by_name || role.role_metadata.responsible_person_name;
                          const authorId = roleAuthorizedByLink(role);
                          // The resident who cleared them is a person in this same app, and
                          // "who authorised this?" is usually followed by "who are they?".
                          return authorId ? (
                            <button
                              onClick={() => openPersonDetail(authorId)}
                              className={cn(
                                "text-sm text-left hover:underline underline-offset-2 transition-colors",
                                darkMode ? "text-gray-200 hover:text-white" : "text-gray-700 hover:text-gray-900"
                              )}
                            >
                              {name}
                            </button>
                          ) : (
                            <p className={cn("text-sm", darkMode ? "text-gray-200" : "text-gray-700")}>{name}</p>
                          );
                        })()}
                      </div>
                    )}

                    {/* Visitor intercom */}
                    {role.role_type === 'visitor' && (
                      <div className="col-span-2">
                        <p className={cn("text-[10px] uppercase tracking-widest mb-0.5", darkMode ? "text-gray-500" : "text-gray-400")}>Interfonar ao chegar</p>
                        <p className={cn("text-sm", darkMode ? "text-gray-200" : "text-gray-700")}>
                          {(role.role_metadata?.requires_intercom ?? true) ? "Sim — avisar morador por interfone" : "Não — entrada direta"}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Vehicles */}
      {person.vehicles && person.vehicles.length > 0 && (() => {
        const activeVehiclePlate = activeVisitorRole?.role_metadata?.vehicle_plate?.toUpperCase();
        return (
          <div>
            <p className={cn("text-[10px] font-semibold uppercase tracking-widest mb-3", darkMode ? "text-gray-500" : "text-gray-400")}>
              Veículos
            </p>
            <div className={cn("rounded-xl border overflow-hidden divide-y", darkMode ? "border-gray-700 divide-gray-700" : "border-gray-200 divide-gray-100")}>
              {person.vehicles.map((vehicle) => {
                const subtitle = [vehicle.brand, vehicle.model, vehicle.color, vehicle.year].filter(Boolean).join(' · ');
                const inUse = !!activeVehiclePlate && vehicle.plate?.toUpperCase() === activeVehiclePlate;
                return (
                  <div
                    key={vehicle.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setVehicleDetail({ ...vehicle, person_id: person.id, person: { id: person.id, first_name: person.first_name, last_name: person.last_name } })}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 transition-colors cursor-pointer",
                      inUse
                        ? darkMode ? "bg-blue-900/30 border-l-2 border-l-blue-500 hover:bg-blue-900/50" : "bg-blue-50 border-l-2 border-l-blue-500 hover:bg-blue-100"
                        : darkMode ? "bg-gray-800/30 hover:bg-gray-700/40" : "bg-white hover:bg-gray-50"
                    )}
                  >
                    <div className={cn(
                      "flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center",
                      inUse ? darkMode ? "bg-blue-800/50" : "bg-blue-100" : darkMode ? "bg-gray-700" : "bg-gray-100"
                    )}>
                      <Car className={cn("w-4 h-4", inUse ? "text-blue-400" : darkMode ? "text-gray-400" : "text-gray-500")} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <LicensePlate plate={vehicle.plate} size="sm" />
                      {subtitle && (
                        <p className={cn("text-xs mt-0.5 truncate", darkMode ? "text-gray-400" : "text-gray-500")}>
                          {subtitle}
                        </p>
                      )}
                    </div>
                    {inUse ? (
                      <span className={cn(
                        "flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide",
                        darkMode ? "bg-blue-900/50 text-blue-300" : "bg-blue-100 text-blue-600"
                      )}>
                        Em uso
                      </span>
                    ) : (
                      <span className={cn(
                        "flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide",
                        vehicle.is_active
                          ? darkMode ? "bg-emerald-900/40 text-emerald-400" : "bg-emerald-50 text-emerald-600"
                          : darkMode ? "bg-gray-700 text-gray-500" : "bg-gray-100 text-gray-400"
                      )}>
                        {vehicle.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* User Account Linking - Only visible to admins (90+) — shown at the bottom of the
          tab, right above the Criado em / Atualizado em metadata card */}
      {canManageUsers && (
        <div className="mt-5">
          {/* Show linked account indicator in view mode */}
          {!isEditing && person.keycloak_user_id && (
            <div>
              <label className={cn(
                "block text-sm font-medium mb-2",
                darkMode ? "text-gray-300" : "text-gray-700"
              )}>
                Conta de Usuário
              </label>
              <div className={cn(
                "flex items-center gap-3 p-3 rounded-lg border",
                darkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              )}>
                <div className={cn(
                  "w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium",
                  darkMode ? "bg-green-900/30 text-green-400" : "bg-green-100 text-green-700"
                )}>
                  <Check className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className={cn("text-sm font-medium", darkMode ? "text-white" : "text-gray-900")}>
                    Conta Vinculada
                  </p>
                  <p className={cn("text-xs", darkMode ? "text-gray-400" : "text-gray-500")}>
                    Esta pessoa possui uma conta de acesso ao sistema
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Invite link — show only for residents/staff/condo employees without an account */}
          {!isEditing && !person.keycloak_user_id && (activeResidentRole || activeStaffRole || activeCondoEmployeeRole) && (
            <div>
              <label className={cn(
                "block text-sm font-medium mb-2",
                darkMode ? "text-gray-300" : "text-gray-700"
              )}>
                Conta de Usuário
              </label>
              <div className={cn(
                "rounded-lg border px-3 py-2.5 flex items-center gap-3",
                darkMode ? "bg-gray-800/50 border-gray-700" : "bg-gray-50 border-gray-200"
              )}>
                <p className={cn("text-xs flex-1", darkMode ? "text-gray-400" : "text-gray-500")}>
                  {inviteUrl
                    ? <span className={cn("font-mono text-xs break-all", darkMode ? "text-green-400" : "text-gray-700")}>{inviteUrl}</span>
                    : 'Sem conta de acesso'}
                </p>

                {inviteUrl ? (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => { navigator.clipboard.writeText(inviteUrl); toast.success('Copiado!'); }}
                      className={cn(
                        "flex items-center gap-1 text-xs px-2 py-1 rounded-md font-medium transition-colors",
                        darkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-200" : "bg-white border border-gray-300 hover:bg-gray-100 text-gray-600"
                      )}
                    >
                      <Copy className="h-3 w-3" />
                      Copiar
                    </button>
                    <button
                      onClick={handleGenerateInvite}
                      className={cn(
                        "p-1 rounded-md transition-colors",
                        darkMode ? "text-gray-500 hover:text-gray-300 hover:bg-gray-700" : "text-gray-400 hover:text-gray-600 hover:bg-gray-200"
                      )}
                      title="Gerar novo link"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleGenerateInvite}
                    disabled={generatingInvite}
                    className={cn(
                      "flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md font-medium transition-colors flex-shrink-0 disabled:opacity-50",
                      darkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-200" : "bg-white border border-gray-300 hover:bg-gray-100 text-gray-700"
                    )}
                  >
                    <Link className="h-3 w-3" />
                    {generatingInvite ? 'Gerando...' : 'Gerar convite'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Show UserLinkingSection in edit mode */}
          {isEditing && (
            <UserLinkingSection
              selectedUser={selectedUser}
              onUserSelect={setSelectedUser}
              disabled={loadingLinkedUser}
              darkMode={darkMode}
            />
          )}
        </div>
      )}

      {/* Metadata Card: Location + Dates + History */}
      {(person.created_at || onShowHistory) && (
        <div className={cn("mt-3 rounded-xl border overflow-hidden", darkMode ? "border-gray-700" : "border-gray-200")}>

          {/* Dates */}
          {person.created_at && (
            <div className={cn("px-4 py-2 grid gap-x-6 gap-y-1.5", (person.updated_at && person.updated_at !== person.created_at) ? "grid-cols-2" : "grid-cols-1")}>
              <div>
                <p className={cn("text-[10px] uppercase tracking-widest font-semibold mb-0.5", darkMode ? "text-gray-500" : "text-gray-400")}>Criado em</p>
                <p className={cn("text-sm", darkMode ? "text-gray-200" : "text-gray-800")}>
                  {new Date(person.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
                {person.created_by_name && (
                  <p className={cn("text-xs mt-0.5", darkMode ? "text-gray-500" : "text-gray-500")}>por {person.created_by_name}</p>
                )}
              </div>
              {person.updated_at && person.updated_at !== person.created_at && (
                <div>
                  <p className={cn("text-[10px] uppercase tracking-widest font-semibold mb-1", darkMode ? "text-gray-500" : "text-gray-400")}>Última modificação</p>
                  <p className={cn("text-sm", darkMode ? "text-gray-200" : "text-gray-800")}>
                    {new Date(person.updated_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* History button */}
          {onShowHistory && (
            <button
              onClick={onShowHistory}
              className={cn(
                "w-full flex items-center justify-between px-4 py-2 border-t text-sm transition-colors",
                darkMode
                  ? "border-gray-700 text-gray-400 hover:text-gray-200 hover:bg-gray-800/50"
                  : "border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-50"
              )}
            >
              <span className="flex items-center gap-2 font-medium">
                <History className="w-3.5 h-3.5" />
                Histórico de Modificações
              </span>
              <ChevronRightIcon className="w-4 h-4 opacity-40" />
            </button>
          )}

        </div>
      )}

      {/* Danger Zone - Delete Person (only in edit mode) */}
      {!readOnly && isEditing && (
        <div className={cn(
          'mt-6 rounded-lg border overflow-hidden',
          hasDeletePermission
            ? darkMode ? 'border-red-900' : 'border-red-200'
            : darkMode ? 'border-yellow-900' : 'border-yellow-200'
        )}>
          {/* Header */}
          <div className={cn(
            'px-3 py-2 border-b',
            hasDeletePermission
              ? darkMode ? 'bg-red-900/20 border-red-900' : 'bg-red-50 border-red-200'
              : darkMode ? 'bg-yellow-900/20 border-yellow-900' : 'bg-yellow-50 border-yellow-200'
          )}>
            <span className={cn(
              'text-[10px] font-semibold uppercase tracking-widest',
              hasDeletePermission
                ? darkMode ? 'text-red-500' : 'text-red-500'
                : darkMode ? 'text-yellow-500' : 'text-yellow-600'
            )}>
              Zona de Perigo
            </span>
          </div>

          {/* Body */}
          <div className={cn(
            'flex items-center justify-between px-3 py-3 gap-4',
            hasDeletePermission
              ? darkMode ? 'bg-red-900/10' : 'bg-red-50/50'
              : darkMode ? 'bg-yellow-900/10' : 'bg-yellow-50/50'
          )}>
            {hasDeletePermission ? (
              <>
                <p className={cn('text-xs flex-1', darkMode ? 'text-red-300' : 'text-red-600')}>
                  A exclusão desta pessoa é permanente e não pode ser desfeita. Todos os dados relacionados serão removidos.
                </p>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={deleting}
                  className="flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Excluindo...' : 'Excluir Pessoa'}
                </button>
              </>
            ) : (
              <p className={cn('text-xs', darkMode ? 'text-yellow-400' : 'text-yellow-700')}>
                Você não possui permissão para excluir pessoas. Entre em contato com um síndico ou administrador.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => !deleting && setShowDeleteConfirm(false)}>
          <div
            className={cn(
              "max-w-md w-full mx-4 p-6 rounded-lg shadow-xl",
              darkMode ? "bg-gray-800" : "bg-white"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5" />
              <div>
                <h3 className={cn(
                  "text-lg font-semibold mb-2",
                  darkMode ? "text-gray-100" : "text-gray-900"
                )}>
                  Confirmar Exclusão
                </h3>
                <p className={cn(
                  "text-sm",
                  darkMode ? "text-gray-300" : "text-gray-600"
                )}>
                  Tem certeza que deseja excluir <strong>{person.first_name} {person.last_name}</strong>?
                  O acesso físico será revogado em todos os dispositivos antes da exclusão. Esta ação não pode ser desfeita.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className={cn(
                  "px-4 py-2 rounded-lg transition-colors disabled:opacity-50",
                  darkMode
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                )}
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteWithRevoke}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {deleting ? 'Excluindo...' : 'Excluir e Revogar Acesso'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Image Zoom Lightbox */}
      {viewingDocumentImage && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setViewingDocumentImage(null)}
        >
          <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setViewingDocumentImage(null)}
              className="absolute -top-3 -right-3 z-10 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg text-gray-700 hover:text-gray-900"
            >
              <X className="w-4 h-4" />
            </button>
            <img
              src={viewingDocumentImage.url}
              alt={viewingDocumentImage.title}
              className="w-full rounded-lg shadow-2xl"
            />
            <p className="text-center text-white/70 text-xs mt-2">{viewingDocumentImage.title}</p>
          </div>
        </div>
      )}

      {/* Security Risk Confirmation Modal (after revoke failures) */}
      {showSecurityRiskConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => !deleting && setShowSecurityRiskConfirm(false)}>
          <div
            className={cn(
              "max-w-md w-full mx-4 p-6 rounded-lg shadow-xl",
              darkMode ? "bg-gray-800" : "bg-white"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-orange-500 mt-0.5" />
              <div>
                <h3 className={cn(
                  "text-lg font-semibold mb-2",
                  darkMode ? "text-gray-100" : "text-gray-900"
                )}>
                  Risco de Segurança
                </h3>
                <p className={cn(
                  "text-sm",
                  darkMode ? "text-gray-300" : "text-gray-600"
                )}>
                  {revokeFailCount} dispositivo(s) não puderam ser revogados fisicamente.
                  A pessoa pode ainda conseguir acesso físico a esses dispositivos.
                  Deseja continuar com a exclusão mesmo assim?
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowSecurityRiskConfirm(false)}
                disabled={deleting}
                className={cn(
                  "px-4 py-2 rounded-lg transition-colors disabled:opacity-50",
                  darkMode
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                )}
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  setShowSecurityRiskConfirm(false);
                  await handleDelete();
                }}
                disabled={deleting}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {deleting ? 'Excluindo...' : 'Continuar mesmo assim'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Detail Modal */}
      {vehicleDetail && person.customer?.id && (
        <VehicleDetailModal
          open={!!vehicleDetail}
          vehicle={vehicleDetail}
          customerId={person.customer.id}
          onClose={() => setVehicleDetail(null)}
          onEdit={(v) => { setVehicleDetail(null); setEditingVehicle(v); }}
          // Same actions the Veículos tab offers — without these the modal drops its whole
          // action bar and the vehicle becomes read-only here for no stated reason.
          onSync={(v) => syncOrRevokeVehicle(v, true)}
          isSyncing={!!(vehicleDetail && syncingVehicleId === vehicleDetail.id)}
          onRevoke={(v) => syncOrRevokeVehicle(v, false)}
          isRevoking={!!(vehicleDetail && revokingVehicleId === vehicleDetail.id)}
          onDelete={(v) => { setVehicleDetail(null); deleteVehicle(v); }}
          onPersonClick={(id) => { setVehicleDetail(null); openPersonDetail(id); }}
          lightTheme={!darkMode}
        />
      )}

      {/* Edit Vehicle Modal — "Editar" in the detail modal used to only close it, so a
          vehicle opened from here could not be edited at all. Same defect as VehiclesTab. */}
      {editingVehicle && person.customer?.id && (
        <VehicleFormModal
          open={!!editingVehicle}
          onClose={() => setEditingVehicle(null)}
          onSuccess={() => { setEditingVehicle(null); onRefresh(); }}
          customerId={person.customer.id}
          customerType={(person.customer?.type as 'condominio' | 'empresa') || 'condominio'}
          darkMode={darkMode}
          editingVehicle={editingVehicle}
          condoType={(person.customer as any)?.condoType || (person.customer as any)?.condo_data?.condo_type}
        />
      )}
    </div>
  );
};
